import { useEffect, useState, useRef, useCallback } from "react";
import api, { API, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useSettings } from "../contexts/SettingsContext";
import CalendarView from "../components/CalendarView";
import MessagingPanel from "../components/MessagingPanel";
import NotificationToggle from "../components/NotificationToggle";
import FeesTab from "../components/FeesTab";
import EventsTab from "../components/EventsTab";
import BackupsTab from "../components/BackupsTab";
import { ArabicLevelBlock } from "../components/ArabicLevelFields";
import { SOURATES } from "../lib/sourates";
import { logger } from "../lib/logger";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Trash2, Plus, Users, BookOpen, Inbox, CheckCircle2, XCircle, Eye, CreditCard, Pencil, Layers, Lock, Unlock, UsersRound, ClipboardList, Download, Mail, Copy, MessageSquare, KeyRound, History, CalendarRange, GraduationCap, Search, SortAsc, SortDesc, UserCog, GripVertical, ChevronDown, ChevronUp, Banknote, Undo2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const MATIERES = [
  { key: "arabe", label: "Arabe" },
  { key: "coran", label: "Coran" },
  { key: "education_musulmane", label: "Éducation Musulmane" },
];
const ML = Object.fromEntries(MATIERES.map((m) => [m.key, m.label]));

function SearchBar({ value, onChange, placeholder, testId }) {
  return (
    <div className="relative flex-1 min-w-[240px]">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8a92a3]" strokeWidth={1.5} />
      <Input
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-9 rounded-full bg-white border-[#EAE6DF]"
        data-testid={testId}
      />
    </div>
  );
}

function matchesRequest(r, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [r.enf_prenom, r.enf_nom, r.rl_prenom, r.rl_nom, r.rl_email, r.enf_ville]
    .filter(Boolean).join(" ").toLowerCase().includes(q);
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const { enrollmentOpen, reenrollmentOpen, refresh: refreshSettings } = useSettings();
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [groups, setGroups] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [tab, setTab] = useState("requests");
  const [togglingEnroll, setTogglingEnroll] = useState(false);
  const [togglingReenroll, setTogglingReenroll] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [requestsSearch, setRequestsSearch] = useState("");
  const [reenrollSearch, setReenrollSearch] = useState("");
  const [years, setYears] = useState([]);
  const [activeYearId, setActiveYearId] = useState(null);  // l'année réellement active côté serveur
  const [selectedYearId, setSelectedYearId] = useState(null);  // celle filtrée par l'admin
  const tabsListRef = useRef(null);

  const isHistoric = activeYearId && selectedYearId && selectedYearId !== activeYearId;

  const loadYears = () => api.get("/school-years").then((r) => {
    setYears(r.data);
    const active = r.data.find((y) => y.is_active);
    if (active) {
      setActiveYearId(active.id);
      if (!selectedYearId) setSelectedYearId(active.id);
    }
  });

  const load = () => {
    const params = selectedYearId ? { year_id: selectedYearId } : {};
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/enrollment-requests", { params }).then((r) => setRequests(r.data));
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/courses", { params }).then((r) => setCourses(r.data));
    api.get("/groups", { params }).then((r) => setGroups(r.data));
    api.get("/vacations", { params }).then((r) => setVacations(r.data));
    api.get("/meetings", { params }).then((r) => setMeetings(r.data));
  };
  useEffect(() => { loadYears(); }, []); // eslint-disable-line
  useEffect(() => { if (selectedYearId) load(); }, [selectedYearId]); // eslint-disable-line

  // Mobile: scroll active tab into view when tab changes
  useEffect(() => {
    const el = tabsListRef.current;
    if (!el || window.innerWidth >= 768) return;
    const active = el.querySelector('[data-state="active"]');
    if (active && typeof active.scrollIntoView === "function") {
      active.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [tab]);

  const toggleEnrollment = async () => {
    setTogglingEnroll(true);
    try {
      await api.post("/settings/enrollment", { enrollment_open: !enrollmentOpen });
      await refreshSettings();
      toast.success(enrollmentOpen ? "Nouvelles inscriptions fermées" : "Nouvelles inscriptions ouvertes");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setTogglingEnroll(false);
    }
  };

  const toggleReenrollment = async () => {
    setTogglingReenroll(true);
    try {
      await api.post("/settings/reenrollment", { reenrollment_open: !reenrollmentOpen });
      await refreshSettings();
      toast.success(reenrollmentOpen ? "Réinscriptions fermées" : "Réinscriptions ouvertes");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setTogglingReenroll(false);
    }
  };

  const teachers = users.filter((u) => u.role === "teacher" || u.is_teacher);
  const groupName = (gid) => groups.find((g) => g.id === gid)?.name || "—";

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-12 fade-in" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C86444]">Administration</span>
          <h1 className="mt-2 font-[Outfit] font-semibold text-3xl md:text-4xl tracking-tight text-[#1A2547]">Tableau de bord</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <NotificationToggle compact />
          <SchoolYearSelector
            years={years}
            selectedYearId={selectedYearId}
            activeYearId={activeYearId}
            onChange={setSelectedYearId}
            onYearsChanged={loadYears}
          />
          {!isHistoric && (
            <>
              <Button
                onClick={toggleEnrollment}
                disabled={togglingEnroll}
                className={`rounded-full h-11 px-5 ${enrollmentOpen ? "bg-[#1E7A4D] hover:bg-[#16633D] text-white" : "bg-[#E2E5EE] text-[#5c6374] hover:bg-[#D4D8E5] border border-[#C5CAD8]"}`}
                data-testid="toggle-enrollment-btn"
                title={enrollmentOpen ? "Cliquer pour fermer les nouvelles inscriptions" : "Cliquer pour ouvrir les nouvelles inscriptions"}
              >
                {enrollmentOpen ? (<><Unlock className="w-4 h-4 mr-2" /> Nouvelles inscriptions ouvertes</>) : (<><Lock className="w-4 h-4 mr-2" /> Nouvelles inscriptions fermées</>)}
              </Button>
              <Button
                onClick={toggleReenrollment}
                disabled={togglingReenroll}
                className={`rounded-full h-11 px-5 ${reenrollmentOpen ? "bg-[#C86444] hover:bg-[#A8533A] text-white" : "bg-[#E2E5EE] text-[#5c6374] hover:bg-[#D4D8E5] border border-[#C5CAD8]"}`}
                data-testid="toggle-reenrollment-btn"
                title={reenrollmentOpen ? "Cliquer pour fermer les réinscriptions" : "Cliquer pour ouvrir les réinscriptions"}
              >
                {reenrollmentOpen ? (<><Unlock className="w-4 h-4 mr-2" /> Réinscriptions ouvertes</>) : (<><Lock className="w-4 h-4 mr-2" /> Réinscriptions fermées</>)}
              </Button>
            </>
          )}
        </div>
      </div>

      {isHistoric && (
        <div className="mt-6 bg-[#FFF5E5] border border-[#C86444]/30 rounded-2xl p-4 flex items-start gap-2" data-testid="historic-banner">
          <Lock className="w-5 h-5 text-[#C86444] shrink-0 mt-0.5" strokeWidth={1.5} />
          <div className="text-sm text-[#1A2547]">
            <strong>Année passée — Lecture seule recommandée</strong>
            <div className="text-[#5c6374]">Vous consultez l'historique de cette année. Les modifications sont possibles mais peuvent affecter les statistiques.</div>
          </div>
        </div>
      )}

      {stats && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat icon={Inbox} label="En attente" value={stats.requests_pending} highlight={stats.requests_pending > 0} />
          <Stat icon={CheckCircle2} label="Validées" value={stats.requests_validated} />
          <Stat icon={Users} label="Enseignants" value={stats.teachers} />
          <Stat icon={BookOpen} label="Groupes" value={groups.length} />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} orientation="vertical" className="mt-10 flex flex-col md:flex-row md:gap-6">
        <TabsList ref={tabsListRef} className="mobile-tabs-fade bg-[#F4F1EB] p-1.5 md:p-2 flex md:flex-col md:items-stretch md:w-60 md:shrink-0 md:h-auto md:rounded-2xl md:sticky md:top-24 md:self-start rounded-full h-auto overflow-x-auto md:overflow-visible gap-1 justify-start max-w-full">
          <TabsTrigger value="messages" className="rounded-full md:rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm md:justify-start md:gap-2.5 md:px-3 md:py-2.5 whitespace-nowrap shrink-0" data-testid="tab-messages"><MessageSquare className="w-4 h-4 hidden md:block" strokeWidth={1.5} /> Messagerie</TabsTrigger>
          <TabsTrigger value="requests" className="rounded-full md:rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm md:justify-start md:gap-2.5 md:px-3 md:py-2.5 whitespace-nowrap shrink-0" data-testid="tab-requests"><Inbox className="w-4 h-4 hidden md:block" strokeWidth={1.5} /> Demandes</TabsTrigger>
          <TabsTrigger value="reenrollments" className="rounded-full md:rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm md:justify-start md:gap-2.5 md:px-3 md:py-2.5 whitespace-nowrap shrink-0" data-testid="tab-reenrollments"><GraduationCap className="w-4 h-4 hidden md:block" strokeWidth={1.5} /> Réinscriptions</TabsTrigger>
          <TabsTrigger value="students" className="rounded-full md:rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm md:justify-start md:gap-2.5 md:px-3 md:py-2.5 whitespace-nowrap shrink-0" data-testid="tab-students"><Users className="w-4 h-4 hidden md:block" strokeWidth={1.5} /> Élèves</TabsTrigger>
          <TabsTrigger value="users" className="rounded-full md:rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm md:justify-start md:gap-2.5 md:px-3 md:py-2.5 whitespace-nowrap shrink-0" data-testid="tab-users"><UserCog className="w-4 h-4 hidden md:block" strokeWidth={1.5} /> Utilisateurs</TabsTrigger>
          <TabsTrigger value="groups" className="rounded-full md:rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm md:justify-start md:gap-2.5 md:px-3 md:py-2.5 whitespace-nowrap shrink-0" data-testid="tab-groups"><Layers className="w-4 h-4 hidden md:block" strokeWidth={1.5} /> Groupes</TabsTrigger>
          <TabsTrigger value="courses" className="rounded-full md:rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm md:justify-start md:gap-2.5 md:px-3 md:py-2.5 whitespace-nowrap shrink-0" data-testid="tab-courses"><BookOpen className="w-4 h-4 hidden md:block" strokeWidth={1.5} /> Cours</TabsTrigger>
          <TabsTrigger value="events" className="rounded-full md:rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm md:justify-start md:gap-2.5 md:px-3 md:py-2.5 whitespace-nowrap shrink-0" data-testid="tab-events"><CalendarRange className="w-4 h-4 hidden md:block" strokeWidth={1.5} /> Événements</TabsTrigger>
          <TabsTrigger value="planning" className="rounded-full md:rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm md:justify-start md:gap-2.5 md:px-3 md:py-2.5 whitespace-nowrap shrink-0" data-testid="tab-planning"><CalendarRange className="w-4 h-4 hidden md:block" strokeWidth={1.5} /> Planning</TabsTrigger>
          <TabsTrigger value="attendance" className="rounded-full md:rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm md:justify-start md:gap-2.5 md:px-3 md:py-2.5 whitespace-nowrap shrink-0" data-testid="tab-attendance"><ClipboardList className="w-4 h-4 hidden md:block" strokeWidth={1.5} /> Présences</TabsTrigger>
          <TabsTrigger value="fees" className="rounded-full md:rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm md:justify-start md:gap-2.5 md:px-3 md:py-2.5 whitespace-nowrap shrink-0" data-testid="tab-fees"><CreditCard className="w-4 h-4 hidden md:block" strokeWidth={1.5} /> Frais d'inscription</TabsTrigger>
          <TabsTrigger value="compensation" className="rounded-full md:rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm md:justify-start md:gap-2.5 md:px-3 md:py-2.5 whitespace-nowrap shrink-0" data-testid="tab-compensation"><Banknote className="w-4 h-4 hidden md:block" strokeWidth={1.5} /> Dédommagements</TabsTrigger>
          <TabsTrigger value="backups" className="rounded-full md:rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm md:justify-start md:gap-2.5 md:px-3 md:py-2.5 whitespace-nowrap shrink-0" data-testid="tab-backups"><Download className="w-4 h-4 hidden md:block" strokeWidth={1.5} /> Sauvegardes</TabsTrigger>
        </TabsList>

        <div className="flex-1 min-w-0">
        <TabsContent value="requests" className="mt-6 md:mt-0 space-y-3">
          <RequestsToolbar requests={requests.filter((r) => !r.is_reenrollment)} groups={groups} statusFilter={statusFilter} onFilterChange={setStatusFilter} />
          <div className="flex flex-wrap items-center gap-2">
            <SearchBar
              value={requestsSearch}
              onChange={setRequestsSearch}
              placeholder="Rechercher un enfant ou un parent…"
              testId="requests-search"
            />
            {(() => {
              const pendingParents = new Set(
                requests
                  .filter((r) => r.status === "validated" && !r.validation_email_sent_at && r.rl_email)
                  .map((r) => r.rl_email.trim().toLowerCase())
              );
              const n = pendingParents.size;
              if (n === 0) return null;
              return (
                <Button
                  size="sm"
                  className="rounded-full bg-[#1E3A8A] hover:bg-[#14265E] text-white h-10"
                  onClick={async () => {
                    if (!window.confirm(`Envoyer l'email de validation (identifiants, rentrée, permanences) à ${n} parent${n > 1 ? "s" : ""} n'ayant pas encore reçu l'email ?\n\nUn seul email sera envoyé par fratrie.`)) return;
                    try {
                      const { data } = await api.post("/enrollment-requests/send-validation-emails-bulk");
                      let msg = `${data.parents_sent} email${data.parents_sent > 1 ? "s" : ""} envoyé${data.parents_sent > 1 ? "s" : ""} (${data.children_covered} enfant${data.children_covered > 1 ? "s" : ""} couverts)`;
                      if (data.failed?.length) msg += ` — ${data.failed.length} échec(s) : ${data.failed.map((f) => f.email).join(", ")}`;
                      (data.failed?.length ? toast.warning : toast.success)(msg, { duration: 8000 });
                      load();
                    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                  }}
                  data-testid="send-all-validation-emails"
                >
                  <Mail className="w-4 h-4 mr-1.5" />
                  Envoyer les emails en attente ({n})
                </Button>
              );
            })()}
          </div>
          {(() => {
            const base = requests.filter((r) => !r.is_reenrollment);
            const filtered = (statusFilter === "all" ? base : base.filter((r) => r.status === statusFilter))
              .filter((r) => matchesRequest(r, requestsSearch));
            if (filtered.length === 0) return <Empty msg={requestsSearch.trim() ? "Aucune demande ne correspond à la recherche." : statusFilter === "all" ? "Aucune nouvelle demande pour le moment." : "Aucune demande dans ce filtre."} />;
            return filtered.map((r) => <RequestCard key={r.id} req={r} groups={groups} groupName={groupName} onAction={load} />);
          })()}
        </TabsContent>

        <TabsContent value="reenrollments" className="mt-6 space-y-3" data-testid="reenrollments-pane">
          {(() => {
            const base = requests.filter((r) => r.is_reenrollment);
            if (base.length === 0) return <Empty msg="Aucune demande de réinscription pour le moment." />;
            const filtered = base.filter((r) => matchesRequest(r, reenrollSearch));
            return (
              <>
                <div className="bg-[#FBE8DC]/60 border border-[#C86444]/30 rounded-2xl p-4 text-sm text-[#1A2547] flex items-start gap-2">
                  <Inbox className="w-5 h-5 text-[#C86444] shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <strong>{base.length} demande{base.length > 1 ? "s" : ""} de réinscription</strong>
                    <div className="text-[#5c6374]">Anciens élèves souhaitant être réinscrits pour l'année active. Les informations sont reprises de leur inscription précédente.</div>
                  </div>
                </div>
                <SearchBar
                  value={reenrollSearch}
                  onChange={setReenrollSearch}
                  placeholder="Rechercher un enfant ou un parent…"
                  testId="reenrollments-search"
                />
                {filtered.length === 0 && <Empty msg="Aucune réinscription ne correspond à la recherche." />}
                {filtered.map((r) => <RequestCard key={r.id} req={r} groups={groups} groupName={groupName} onAction={load} />)}
              </>
            );
          })()}
        </TabsContent>

        <TabsContent value="planning" className="mt-6 md:mt-0">
          <CalendarView courses={courses} vacations={vacations} meetings={meetings} groupName={groupName} initialMode="week" />
        </TabsContent>

        <TabsContent value="groups" className="mt-6 md:mt-0">
          <GroupsTab groups={groups} requests={requests} onSaved={load} />
        </TabsContent>

        <TabsContent value="courses" className="mt-6 md:mt-0">
          <CoursesGrid courses={courses} groups={groups} teachers={teachers} onSaved={load} />
        </TabsContent>

        <TabsContent value="users" className="mt-6 md:mt-0">
          <div className="flex justify-end mb-4">
            <UserDialog onSaved={load} />
          </div>
          <UsersTable users={users} onChanged={load} />
        </TabsContent>

        <TabsContent value="students" className="mt-6 md:mt-0" data-testid="students-pane">
          <StudentsTab requests={requests} groups={groups} groupName={groupName} courses={courses} onSaved={load} />
        </TabsContent>

        <TabsContent value="events" className="mt-6 md:mt-0" data-testid="events-pane">
          <EventsTab vacations={vacations} meetings={meetings} courses={courses} groups={groups} onSaved={load} />
        </TabsContent>

        <TabsContent value="attendance" className="mt-6 md:mt-0">
          <AttendanceTab groups={groups} courses={courses} />
        </TabsContent>

        <TabsContent value="fees" className="mt-6 md:mt-0" data-testid="fees-pane">
          <FeesTab selectedYearId={selectedYearId} />
        </TabsContent>

        <TabsContent value="backups" className="mt-6 md:mt-0" data-testid="backups-pane">
          <BackupsTab selectedYearId={selectedYearId} />
        </TabsContent>

        <TabsContent value="messages" className="mt-6 md:mt-0 space-y-6">
          <MessagingPanel currentUser={user} />
          <AdminMessagesModeration />
        </TabsContent>

        <TabsContent value="compensation" className="mt-6 md:mt-0">
          <CompensationTab users={users} />
        </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

function Stat({ icon: Icon, label, value, highlight }) {
  return (
    <div className={`bg-white border rounded-xl p-5 ${highlight ? "border-[#C86444] shadow-[0_8px_24px_rgba(200,100,68,0.12)]" : "border-[#EAE6DF]"}`}>
      <Icon className={`w-5 h-5 ${highlight ? "text-[#C86444]" : "text-[#1E3A8A]"}`} strokeWidth={1.5} />
      <div className="mt-3 text-2xl font-[Outfit] font-semibold text-[#1A2547]">{value}</div>
      <div className="text-xs uppercase tracking-[0.18em] text-[#8a968f] mt-1">{label}</div>
    </div>
  );
}

function Empty({ msg }) {
  return <div className="bg-white border border-[#EAE6DF] rounded-2xl p-10 text-center text-[#5C6861]">{msg}</div>;
}

function RequestCard({ req, groups, groupName, onAction }) {
  const statusBadge = req.status === "pending"
    ? <Badge className="bg-[#FFF5E5] text-[#C86444] hover:bg-[#FFF5E5] border-0 rounded-full">En attente</Badge>
    : req.status === "validated"
      ? <Badge className="bg-[#E0E7FF] text-[#1E7A4D] hover:bg-[#E0E7FF] border-0 rounded-full">Validée</Badge>
      : <Badge className="bg-[#FBE5E4] text-[#B0413E] hover:bg-[#FBE5E4] border-0 rounded-full">Rejetée</Badge>;

  return (
    <div className="bg-white border border-[#EAE6DF] rounded-2xl p-5" data-testid={`request-${req.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-[Outfit] font-semibold text-lg text-[#1A2547]">{req.enf_prenom} {req.enf_nom}</h3>
            {statusBadge}
            {req.is_reenrollment && (
              <Badge className="bg-[#FBE8DC] text-[#C86444] hover:bg-[#FBE8DC] border-0 rounded-full inline-flex items-center gap-1" data-testid={`reenrollment-badge-${req.id}`}>
                <GraduationCap className="w-3 h-3" /> Réinscription
              </Badge>
            )}
            {req.a_des_freres_soeurs && (
              <Badge className="bg-[#FFF5E5] text-[#C86444] hover:bg-[#FFF5E5] border-0 rounded-full inline-flex items-center gap-1" data-testid={`fratrie-badge-${req.id}`}>
                <UsersRound className="w-3 h-3" /> Fratrie
              </Badge>
            )}
          </div>
          <div className="text-sm text-[#5C6861] mt-1">
            {req.enf_age ? `${req.enf_age} ans · ` : ""}{req.enf_ville} · Parent : {req.rl_prenom} {req.rl_nom} · {req.rl_email}
          </div>
          {req.assigned_group_id && (
            <div className="text-sm mt-1">
              <span className="text-[#5C6861]">Groupe attribué : </span>
              <strong className="text-[#1E3A8A]">{groupName(req.assigned_group_id)}</strong>
              <span className="text-[#5C6861]"> · {req.assigned_matieres.map((m) => ML[m]).join(", ")}</span>
            </div>
          )}
          {req.status === "validated" && (
            <div className="text-xs mt-1.5" data-testid={`email-status-${req.id}`}>
              {req.validation_email_sent_at ? (
                <span className="inline-flex items-center gap-1 text-[#1E7A4D]">
                  <Mail className="w-3 h-3" /> Email envoyé le {new Date(req.validation_email_sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[#C86444]">
                  <Mail className="w-3 h-3" /> Email au parent non envoyé
                </span>
              )}
            </div>
          )}
          {req.status === "rejected" && (
            <div className="text-xs mt-1.5" data-testid={`rejection-email-status-${req.id}`}>
              {req.rejection_email_sent_at ? (
                <span className="inline-flex items-center gap-1 text-[#1E7A4D]">
                  <Mail className="w-3 h-3" /> Email de refus envoyé le {new Date(req.rejection_email_sent_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[#C86444]">
                  <Mail className="w-3 h-3" /> Email de refus non envoyé
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <ViewDialog req={req} groupName={groupName} />
          {req.status === "pending" && <ValidateDialog req={req} groups={groups} onDone={onAction} />}
          {req.status === "pending" && <RejectDialog req={req} onDone={onAction} />}
          {req.status === "validated" && (
            <>
              <Button size="sm" className="rounded-full bg-[#1E7A4D] hover:bg-[#16633D] text-white"
                onClick={async () => {
                  const already = !!req.validation_email_sent_at;
                  const msg = already
                    ? `Renvoyer l'email de validation à ${req.rl_prenom} ${req.rl_nom} (${req.rl_email}) ?\n\nUn seul email sera envoyé pour toute la fratrie (tous les enfants validés de ce parent).\nSi le parent n'a jamais changé son mot de passe, un nouveau mot de passe temporaire sera généré et inclus.`
                    : `Envoyer l'email de validation à ${req.rl_prenom} ${req.rl_nom} (${req.rl_email}) ?\n\nUn seul email sera envoyé pour toute la fratrie (tous les enfants validés de ce parent), avec les identifiants de connexion si c'est un nouveau compte.`;
                  if (!window.confirm(msg)) return;
                  try {
                    const { data } = await api.post(`/enrollment-requests/${req.id}/send-validation-email`);
                    const nb = data.children_count || 1;
                    const suffix = nb > 1 ? ` — couvre toute la fratrie (${data.children.join(", ")})` : "";
                    toast.success((data.included_credentials
                      ? "Email envoyé (avec identifiants)"
                      : "Email envoyé (sans mot de passe)") + suffix);
                    onAction();
                  } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                }}
                data-testid={`send-email-${req.id}`}
                title={req.validation_email_sent_at ? "Renvoyer l'email de validation" : "Envoyer l'email de validation au parent"}
              >
                <Mail className="w-4 h-4 mr-1.5" />
                {req.validation_email_sent_at ? "Renvoyer l'email" : "Envoyer l'email"}
              </Button>
              <Button size="sm" variant="outline" className="rounded-full border-[#C86444] text-[#C86444] hover:bg-[#FBE8DC]"
                onClick={async () => {
                  if (!window.confirm(`Annuler la validation de ${req.enf_prenom} ${req.enf_nom} ?\n\nLa demande repassera "En attente". Les inscriptions aux cours et le statut payé seront supprimés.\n\nLe compte parent (s'il existe) sera conservé.`)) return;
                  try {
                    await api.post(`/enrollment-requests/${req.id}/unvalidate`);
                    toast.success("Validation annulée — demande remise en attente");
                    onAction();
                  } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                }}
                data-testid={`unvalidate-${req.id}`}>
                <XCircle className="w-4 h-4 mr-1.5" /> Annuler la validation
              </Button>
            </>
          )}
          {req.status === "rejected" && (
            <>
              <Button size="sm" className="rounded-full bg-[#1E3A8A] hover:bg-[#14265E] text-white"
                onClick={async () => {
                  const already = !!req.rejection_email_sent_at;
                  const msg = `${already ? "Renvoyer" : "Envoyer"} l'email de refus à ${req.rl_prenom} ${req.rl_nom} (${req.rl_email}) ?\n\nUn seul email sera envoyé pour toutes les demandes rejetées de ce parent.\nMessage : refus pour manque de places, demande conservée si des places se libèrent.`;
                  if (!window.confirm(msg)) return;
                  try {
                    const { data } = await api.post(`/enrollment-requests/${req.id}/send-rejection-email`);
                    const suffix = (data.children_count || 1) > 1 ? ` — couvre toute la fratrie (${data.children.join(", ")})` : "";
                    toast.success("Email de refus envoyé" + suffix);
                    onAction();
                  } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                }}
                data-testid={`send-rejection-email-${req.id}`}
                title={req.rejection_email_sent_at ? "Renvoyer l'email de refus" : "Envoyer l'email de refus au parent"}
              >
                <Mail className="w-4 h-4 mr-1.5" />
                {req.rejection_email_sent_at ? "Renvoyer l'email" : "Envoyer l'email"}
              </Button>
              <Button size="sm" variant="outline" className="rounded-full border-[#B0413E] text-[#B0413E] hover:bg-[#FBE5E4]"
              onClick={async () => {
                if (!window.confirm(`Supprimer définitivement la demande de ${req.enf_prenom} ${req.enf_nom} ?\n\nCette action est irréversible.`)) return;
                try {
                  await api.delete(`/enrollment-requests/${req.id}`);
                  toast.success("Demande supprimée");
                  onAction();
                } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
              }}
              data-testid={`delete-request-${req.id}`}>
              <Trash2 className="w-4 h-4 mr-1.5" /> Supprimer
              </Button>
            </>
          )}
          {req.status !== "rejected" && (
            <Button size="sm" variant="outline" className="rounded-full border-[#B0413E] text-[#B0413E] hover:bg-[#FBE5E4]"
              onClick={async () => {
                const warn = req.status === "validated"
                  ? `Supprimer définitivement la demande de ${req.enf_prenom} ${req.enf_nom} ?\n\n⚠️ Cette demande est VALIDÉE. Tous les éléments liés seront aussi supprimés (inscriptions aux cours, présences, paiement famille si plus d'enfants).\n\nLe compte parent (s'il existe) sera conservé.\n\nAction irréversible.`
                  : `Supprimer définitivement la demande de ${req.enf_prenom} ${req.enf_nom} ?\n\nCette action est irréversible.`;
                if (!window.confirm(warn)) return;
                try {
                  await api.delete(`/enrollment-requests/${req.id}`);
                  toast.success("Demande supprimée");
                  onAction();
                } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
              }}
              data-testid={`delete-request-${req.id}`}>
              <Trash2 className="w-4 h-4 mr-1.5" /> Supprimer
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function ViewDialog({ req, groupName }) {
  const [photoUrl, setPhotoUrl] = useState(null);
  useEffect(() => {
    if (!req.photo_path) return;
    let revoked = null;
    api.get(`/files/${req.photo_path}`, { responseType: "blob" }).then((r) => {
      const url = URL.createObjectURL(r.data);
      revoked = url;
      setPhotoUrl(url);
    }).catch(() => {});
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [req.photo_path]);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="sm" variant="ghost" className="rounded-full" data-testid={`view-request-${req.id}`}><Eye className="w-4 h-4 mr-1.5" /> Voir détails</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{req.enf_prenom} {req.enf_nom}</DialogTitle></DialogHeader>
        {req.created_at && (
          <div className="text-xs text-[#5c6374] -mt-2" data-testid="request-created-at">
            Demande déposée le <strong className="text-[#1A2547]">{new Date(req.created_at).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" })}</strong>
            {req.is_reenrollment && <span className="ml-2 text-[#C86444]">· Réinscription</span>}
          </div>
        )}
        {photoUrl && (
          <img src={photoUrl} alt="Photo" className="w-32 h-32 rounded-xl object-cover border border-[#EAE6DF]" />
        )}
        <DetailSection title="Représentant légal">
          <DR k="Nom" v={`${req.rl_prenom} ${req.rl_nom}`} />
          <DR k="Email" v={req.rl_email} />
          <DR k="Téléphone portable" v={req.rl_tel_portable} />
          {req.rl_tel_fixe && <DR k="Téléphone fixe" v={req.rl_tel_fixe} />}
          <DR k="Activités à la Mosquée" v={req.rl_participe_activites_mosquee ? (req.rl_activites_mosquee || "Oui") : "Non"} />
          <DR k="Prières régulières à la Mosquée" v={req.rl_prieres_regulieres_mosquee ? "Oui" : "Non"} />
        </DetailSection>
        <DetailSection title="Enfant">
          <DR k="Adresse" v={`${req.enf_adresse}, ${req.enf_ville}`} />
          <DR k="Date de naissance" v={`${req.enf_date_naissance} (${req.enf_age || "?"} ans)`} />
          <DR k="Rentre seul après les cours" v={req.enf_autorisation_rentrer_seul ? "Oui" : "Non"} />
          <DR k="A des frères/sœurs à la Mosquée" v={req.a_des_freres_soeurs ? (req.freres_soeurs_details || "Oui") : "Non"} />
          <DR k="Première inscription" v={req.premiere_inscription ? "Oui" : "Non"} />
          {req.cours_arabe_anterieur && <DR k="Cours d'arabe antérieurs" v={req.cours_arabe_anterieur} />}
          {req.groupe_souhaite && <DR k="Groupe souhaité" v={req.groupe_souhaite} />}
        </DetailSection>
        <DetailSection title="Niveau d'arabe & Coran">
          <DR k="Arabe écrit" v={(["Aucun", "Débutant", "Intermédiaire", "Confirmé", "Avancé", "Bilingue"][req.niveau_arabe_ecrit ?? 0])} />
          <DR k="Arabe oral" v={(["Aucun", "Débutant", "Intermédiaire", "Confirmé", "Avancé", "Bilingue"][req.niveau_arabe_oral ?? 0])} />
          <DR k="Nombre de sourates" v={`${req.nb_sourates ?? 0} / 114`} />
          {Array.isArray(req.sourates_connues) && req.sourates_connues.length > 0 && (
            <DR k="Sourates connues" v={req.sourates_connues.map((n) => {
              const s = SOURATES.find((x) => x.num === n);
              return s ? `${s.num}. ${s.name}` : n;
            }).join(" · ")} />
          )}
        </DetailSection>
        <DetailSection title="Médical">
          <DR k="Traitement médical" v={req.traitement_medical ? (req.traitement_medical_detail || "Oui") : "Non"} />
          <DR k="Allergies" v={req.allergies ? (req.allergies_detail || "Oui") : "Non"} />
          <DR k="Allergies alimentaires" v={req.allergies_alimentaires ? (req.allergies_alimentaires_detail || "Oui") : "Non"} />
          <DR k="Maladies chroniques" v={req.maladies_chroniques ? (req.maladies_chroniques_detail || "Oui") : "Non"} />
          {req.autres_renseignements && <DR k="Autres" v={req.autres_renseignements} />}
        </DetailSection>
        <DetailSection title="Contact urgence">
          <DR k="Nom" v={`${req.urg_prenom} ${req.urg_nom}`} />
          <DR k="Téléphone portable" v={req.urg_tel_portable} />
          {req.urg_tel_fixe && <DR k="Téléphone fixe" v={req.urg_tel_fixe} />}
        </DetailSection>
        <DetailSection title="Demande">
          <DR k="Matières souhaitées" v={req.matieres_suivies.map((m) => ML[m]).join(", ")} />
          <DR k="Autorisation photos en classe" v={req.autorisation_photos_classe ? "Oui" : "Non"} />
          {req.rejection_reason && <DR k="Motif de rejet" v={req.rejection_reason} />}
        </DetailSection>
      </DialogContent>
    </Dialog>
  );
}

function DR({ k, v }) {
  return (
    <div className="flex justify-between gap-4 py-1 border-b border-[#EAE6DF] last:border-0 text-sm">
      <span className="text-[#8a968f]">{k}</span>
      <span className="text-[#1A2547] text-right">{v}</span>
    </div>
  );
}
function DetailSection({ title, children }) {
  return (
    <div>
      <h4 className="text-xs uppercase tracking-[0.2em] font-semibold text-[#C86444] mt-4 mb-2">{title}</h4>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function ValidateDialog({ req, groups, onDone }) {
  const [open, setOpen] = useState(false);
  const [groupId, setGroupId] = useState(groups[0]?.id || "");
  const [matieres, setMatieres] = useState(req.matieres_suivies);
  const [siblingsHint, setSiblingsHint] = useState([]);

  useEffect(() => { if (groups.length > 0 && !groupId) setGroupId(groups[0].id); }, [groups, groupId]);

  useEffect(() => {
    if (!open || !req.a_des_freres_soeurs) return;
    api.get("/enrollment-requests").then((r) => {
      const others = r.data.filter(
        (x) => x.id !== req.id && x.status === "validated" &&
          x.enf_nom?.toLowerCase() === req.enf_nom?.toLowerCase()
      );
      setSiblingsHint(others);
    }).catch(() => {});
  }, [open, req]);

  const submit = async () => {
    if (!groupId) return toast.error("Créez d'abord un groupe dans l'onglet 'Groupes'");
    if (matieres.length === 0) return toast.error("Sélectionnez au moins une matière");
    try {
      const { data } = await api.post(`/enrollment-requests/${req.id}/validate`, { group_id: groupId, matieres });
      const gName = groups.find((g) => g.id === groupId)?.name || "";
      toast.success(`Inscription validée : ${gName} (${matieres.length} matière(s))`);
      if (data?.parent_account_created) {
        toast.info("Compte parent créé. Cliquez sur \"Envoyer l'email\" quand vous serez prêt à communiquer les identifiants.", { duration: 6000 });
      } else if (data?.parent_account_existed) {
        toast.success(`Enfant rattaché au compte parent existant (${data.parent_email})`);
      }
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="rounded-full bg-[#1E3A8A] hover:bg-[#14265E]" data-testid={`validate-${req.id}`}>
          <CheckCircle2 className="w-4 h-4 mr-1.5" /> Valider
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Valider l'inscription de {req.enf_prenom} {req.enf_nom}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {req.a_des_freres_soeurs && (
            <div className="bg-[#FFF5E5] border border-[#C86444]/30 rounded-xl p-3 text-sm" data-testid="fratrie-hint">
              <div className="flex items-start gap-2 text-[#C86444]">
                <UsersRound className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
                <div>
                  <strong className="block text-[#1A2547]">Fratrie signalée par le parent</strong>
                  <span className="text-[#5c6374]">{req.freres_soeurs_details}</span>
                  {siblingsHint.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-[#C86444]/20 text-[#1A2547]">
                      <strong>Frères/sœurs déjà validés (même nom de famille) :</strong>
                      <ul className="mt-1 space-y-0.5">
                        {siblingsHint.map((s) => {
                          const g = groups.find((x) => x.id === s.assigned_group_id);
                          return (
                            <li key={s.id} className="text-xs">
                              · {s.enf_prenom} {s.enf_nom} → <strong>{g?.name || "?"}</strong>
                            </li>
                          );
                        })}
                      </ul>
                      <div className="text-xs text-[#5c6374] mt-1.5 italic">Astuce : choisissez un groupe dont les cours tombent le même jour pour faciliter l'organisation des parents.</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {groups.length === 0 ? (
            <div className="bg-[#FFF5E5] text-[#C86444] text-sm p-3 rounded-xl border border-[#E8A365]/30">
              Aucun groupe défini. Créez d'abord un groupe dans l'onglet « Groupes ».
            </div>
          ) : (
            <>
              <div>
                <Label className="text-sm">Groupe attribué</Label>
                <Select value={groupId} onValueChange={setGroupId}>
                  <SelectTrigger className="mt-1.5 rounded-xl" data-testid="select-group"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Matières assignées</Label>
                <div className="mt-2 space-y-2">
                  {MATIERES.map((m) => (
                    <label key={m.key} className="flex items-center gap-2 p-2.5 rounded-xl border border-[#EAE6DF] cursor-pointer hover:bg-[#FCFBF8]">
                      <Checkbox
                        checked={matieres.includes(m.key)}
                        onCheckedChange={() => setMatieres((cur) => cur.includes(m.key) ? cur.filter((x) => x !== m.key) : [...cur, m.key])}
                        data-testid={`assign-${m.key}`}
                      />
                      <span className="text-sm">{m.label}</span>
                    </label>
                  ))}
                </div>
                <div className="text-xs text-[#8a968f] mt-2">Demandé : {req.matieres_suivies.map((m) => ML[m]).join(", ")}</div>
              </div>
              <div className="bg-[#E0E7FF] border border-[#1E3A8A]/20 rounded-xl p-3 text-xs text-[#1A2547] flex items-start gap-2">
                <Mail className="w-4 h-4 mt-0.5 shrink-0 text-[#1E3A8A]" strokeWidth={1.5} />
                <div>
                  <strong>L'email au parent ne sera PAS envoyé automatiquement.</strong>{" "}
                  Une fois la validation confirmée, utilisez le bouton <em>"Envoyer l'email"</em> sur la fiche pour communiquer les identifiants au parent — quand tout est prêt (groupes, salles, planning...).
                </div>
              </div>
              <Button onClick={submit} className="w-full rounded-full bg-[#1E3A8A] hover:bg-[#14265E]" data-testid="confirm-validate">Confirmer la validation</Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({ req, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const submit = async () => {
    await api.post(`/enrollment-requests/${req.id}/reject`, { reason: reason || null });
    toast.success("Demande rejetée");
    setOpen(false);
    onDone();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-full border-[#B0413E] text-[#B0413E]" data-testid={`reject-${req.id}`}>
          <XCircle className="w-4 h-4 mr-1.5" /> Rejeter
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Rejeter la demande</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Label className="text-sm">Motif (optionnel)</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} className="rounded-xl" />
          <Button onClick={submit} variant="outline" className="w-full rounded-full border-[#B0413E] text-[#B0413E]" data-testid="confirm-reject">Confirmer le rejet</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CoursesGrid({ courses, groups, teachers, onSaved }) {
  if (courses.length === 0) return <Empty msg="Aucun cours. Créez d'abord un groupe dans l'onglet « Groupes »." />;
  const byGroup = {};
  for (const c of courses) { byGroup[c.group_id] = byGroup[c.group_id] || []; byGroup[c.group_id].push(c); }
  // Trier les cours de chaque groupe par order
  Object.values(byGroup).forEach((arr) => arr.sort((a, b) => (a.order || 99) - (b.order || 99)));
  const groupOrder = groups.map((g) => g.id).filter((id) => byGroup[id]);
  return (
    <div className="space-y-8">
      <p className="text-xs text-[#8a92a3]">Glissez-déposez les matières par la poignée <GripVertical className="inline w-3 h-3 align-middle" /> pour les réorganiser au sein d'un groupe.</p>
      {groupOrder.map((gid) => {
        const g = groups.find((x) => x.id === gid);
        return (
          <CoursesByGroupBlock
            key={gid}
            groupId={gid}
            groupName={g?.name}
            courses={byGroup[gid]}
            teachers={teachers}
            onSaved={onSaved}
          />
        );
      })}
    </div>
  );
}

function CoursesByGroupBlock({ groupId, groupName, courses, teachers, onSaved }) {
  const [localOrder, setLocalOrder] = useState(courses);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setLocalOrder(courses); }, [courses]);

  const onDragStart = (i) => (e) => {
    setDragIndex(i);
    e.dataTransfer.effectAllowed = "move";
    // setData peut throw sur certains anciens navigateurs ; uniquement requis pour Firefox legacy → ignorer en silence
    try { e.dataTransfer.setData("text/plain", String(i)); } catch (legacyErr) { logger.debug("dataTransfer.setData unsupported:", legacyErr); }
  };
  const onDragOver = (i) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (overIndex !== i) setOverIndex(i);
  };
  const onDragLeave = () => setOverIndex(null);
  const onDragEnd = () => { setDragIndex(null); setOverIndex(null); };

  const persistOrder = async (next) => {
    setSaving(true);
    try {
      await api.post("/courses/reorder", { group_id: groupId, ordered_ids: next.map((c) => c.id) });
      toast.success("Ordre des matières mis à jour");
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setLocalOrder(courses);
    } finally {
      setSaving(false);
    }
  };

  const onDrop = (i) => (e) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === i) { onDragEnd(); return; }
    const next = [...localOrder];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(i, 0, moved);
    setLocalOrder(next);
    onDragEnd();
    persistOrder(next);
  };

  return (
    <div data-testid={`courses-group-${groupId}`}>
      <h3 className="font-[Outfit] font-semibold text-lg text-[#1A2547] mb-3">{groupName}</h3>
      <div className="grid md:grid-cols-3 gap-3">
        {localOrder.map((c, i) => {
          const isDragging = dragIndex === i;
          const isOver = overIndex === i && dragIndex !== null && dragIndex !== i;
          return (
            <div
              key={c.id}
              draggable={!saving}
              onDragStart={onDragStart(i)}
              onDragOver={onDragOver(i)}
              onDragLeave={onDragLeave}
              onDrop={onDrop(i)}
              onDragEnd={onDragEnd}
              className={`relative transition-all ${
                isDragging ? "opacity-40" : isOver ? "ring-2 ring-[#1E3A8A] ring-offset-1" : ""
              } ${saving ? "cursor-wait" : ""}`}
              data-testid={`course-card-wrap-${c.id}`}
            >
              <div
                className="absolute top-3 left-3 cursor-grab active:cursor-grabbing text-[#8a92a3] hover:text-[#1A2547] z-10 bg-white/80 backdrop-blur rounded-md p-0.5"
                title="Glisser pour réorganiser"
                data-testid={`course-handle-${c.id}`}
              >
                <GripVertical className="w-4 h-4" strokeWidth={1.5} />
              </div>
              <CourseEditCard course={c} teachers={teachers} onSaved={onSaved} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

const DAY_OPTIONS = ["Samedi", "Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi"];
const TIME_OPTIONS = (() => {
  const out = [];
  for (let h = 8; h <= 21; h++) for (let m = 0; m < 60; m += 5) {
    if (h === 21 && m > 0) break;
    out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  }
  return out;
})();

function parseScheduleTime(t) {
  const m = String(t || "").match(/(\d{1,2}:\d{2})\s*[-–—]\s*(\d{1,2}:\d{2})/);
  return m ? [m[1].padStart(5, "0"), m[2].padStart(5, "0")] : ["", ""];
}

function CourseEditCard({ course, teachers, onSaved }) {
  const [open, setOpen] = useState(false);
  const [start0, end0] = parseScheduleTime(course.schedule_time);
  const [form, setForm] = useState({
    teacher_ids: course.teacher_ids || [],
    schedule_day: course.schedule_day || "none",
    start: start0 || "none",
    end: end0 || "none",
    location: course.location || "",
    capacity: course.capacity || 20,
  });
  const toggleTeacher = (tid) => {
    setForm((f) => ({
      ...f,
      teacher_ids: f.teacher_ids.includes(tid) ? f.teacher_ids.filter((x) => x !== tid) : [...f.teacher_ids, tid],
    }));
  };
  const submit = async () => {
    const schedule_time = form.start !== "none" && form.end !== "none" ? `${form.start} - ${form.end}` : "";
    await api.put(`/courses/${course.id}`, {
      teacher_ids: form.teacher_ids,
      schedule_day: form.schedule_day !== "none" ? form.schedule_day : "",
      schedule_time,
      location: form.location,
      capacity: parseInt(form.capacity),
    });
    toast.success("Cours mis à jour");
    setOpen(false); onSaved();
  };
  const startOptions = TIME_OPTIONS.includes(start0) || !start0 ? TIME_OPTIONS : [start0, ...TIME_OPTIONS];
  const endOptions = (TIME_OPTIONS.includes(end0) || !end0 ? TIME_OPTIONS : [end0, ...TIME_OPTIONS])
    .filter((t) => form.start === "none" || t > form.start);
  return (
    <div className="bg-white border border-[#EAE6DF] rounded-xl p-4">
      <Badge className="bg-[#E0E7FF] text-[#1E3A8A] hover:bg-[#E0E7FF] border-0 rounded-full mb-2">{ML[course.matiere]}</Badge>
      <div className="text-sm text-[#1A2547] font-medium">{course.schedule_day} · {course.schedule_time}</div>
      <div className="text-xs text-[#8a968f] mt-1">{course.location} · {course.teacher_name}</div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button size="sm" variant="ghost" className="rounded-full mt-3 text-[#1E3A8A]" data-testid={`edit-course-${course.id}`}>Modifier</Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle>{course.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-sm">Enseignants</Label>
              <div className="mt-1.5 border border-[#EAE6DF] rounded-xl max-h-44 overflow-y-auto divide-y divide-[#F2EFE9]" data-testid={`course-teachers-list-${course.id}`}>
                {teachers.length === 0 && (
                  <div className="px-3 py-2 text-xs text-[#8a968f]">Aucun enseignant disponible</div>
                )}
                {teachers.map((t) => (
                  <label key={t.id} className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-[#FCFBF8]">
                    <Checkbox
                      checked={form.teacher_ids.includes(t.id)}
                      onCheckedChange={() => toggleTeacher(t.id)}
                      data-testid={`course-teacher-check-${course.id}-${t.id}`}
                    />
                    <span className="text-sm text-[#1A2547]">{t.name}</span>
                  </label>
                ))}
              </div>
              <div className="text-xs text-[#8a968f] mt-1">
                {form.teacher_ids.length === 0 ? "Non défini" : `${form.teacher_ids.length} enseignant(s) sélectionné(s)`}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3">
              <div><Label className="text-sm">Jour</Label>
                <Select value={form.schedule_day} onValueChange={(v) => setForm({ ...form, schedule_day: v })}>
                  <SelectTrigger className="rounded-xl mt-1.5" data-testid={`course-day-select-${course.id}`}><SelectValue placeholder="Non défini" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Non défini</SelectItem>
                    {DAY_OPTIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-sm">Heure de début</Label>
                  <Select value={form.start} onValueChange={(v) => setForm({ ...form, start: v, end: form.end !== "none" && form.end <= v ? "none" : form.end })}>
                    <SelectTrigger className="rounded-xl mt-1.5" data-testid={`course-start-select-${course.id}`}><SelectValue placeholder="Non défini" /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      <SelectItem value="none">Non défini</SelectItem>
                      {startOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-sm">Heure de fin</Label>
                  <Select value={form.end} onValueChange={(v) => setForm({ ...form, end: v })}>
                    <SelectTrigger className="rounded-xl mt-1.5" data-testid={`course-end-select-${course.id}`}><SelectValue placeholder="Non défini" /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      <SelectItem value="none">Non défini</SelectItem>
                      {endOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div><Label className="text-sm">Lieu</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-xl mt-1.5" /></div>
            <div><Label className="text-sm">Capacité</Label><Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="rounded-xl mt-1.5" /></div>
            <Button onClick={submit} className="w-full rounded-full bg-[#1E3A8A] hover:bg-[#14265E]">Enregistrer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ROLE_LABELS = { admin: "Admin", teacher: "Enseignant", parent: "Parent" };

function UsersTable({ users, onChanged }) {
  const [search, setSearch] = useState("");
  const [nomF, setNomF] = useState("");
  const [emailF, setEmailF] = useState("");
  const [roleF, setRoleF] = useState("all");
  const [telF, setTelF] = useState("");
  const [compF, setCompF] = useState("all");

  const q = search.trim().toLowerCase();
  const filtered = users
    .filter((u) => !q || [u.name, u.email, u.role, u.phone].filter(Boolean).join(" ").toLowerCase().includes(q))
    .filter((u) => !nomF.trim() || (u.name || "").toLowerCase().includes(nomF.trim().toLowerCase()))
    .filter((u) => !emailF.trim() || (u.email || "").toLowerCase().includes(emailF.trim().toLowerCase()))
    .filter((u) => roleF === "all" || u.role === roleF)
    .filter((u) => !telF.trim() || (u.phone || "").toLowerCase().includes(telF.trim().toLowerCase()))
    .filter((u) => {
      if (compF === "all") return true;
      if (compF === "yes") return u.role === "teacher" && !!u.is_compensated;
      return u.role === "teacher" && !u.is_compensated;
    });

  const filterInput = (value, onChange, placeholder, testId) => (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-8 text-xs rounded-lg bg-white border-[#EAE6DF] font-normal normal-case tracking-normal"
      data-testid={testId}
    />
  );

  return (
    <div>
      <div className="mb-4 flex">
        <SearchBar value={search} onChange={setSearch} placeholder="Rechercher un utilisateur (nom, email, rôle)…" testId="users-search" />
      </div>
      <div className="bg-white border border-[#EAE6DF] rounded-2xl overflow-x-auto">
      <table className="w-full min-w-[640px]">
        <thead>
          <tr className="border-b border-[#EAE6DF]">
          {["Nom", "Email", "Rôle", "Téléphone", "Dédommagement", ""].map((h) => (
            <th key={h} className="text-left px-6 py-3 text-xs uppercase tracking-[0.18em] font-semibold text-[#8a968f]">{h}</th>
          ))}
          </tr>
          <tr className="border-b border-[#EAE6DF] bg-[#FCFBF8]">
            <th className="px-6 py-2">{filterInput(nomF, setNomF, "Filtrer…", "users-filter-nom")}</th>
            <th className="px-6 py-2">{filterInput(emailF, setEmailF, "Filtrer…", "users-filter-email")}</th>
            <th className="px-6 py-2">
              <Select value={roleF} onValueChange={setRoleF}>
                <SelectTrigger className="h-8 text-xs rounded-lg bg-white border-[#EAE6DF] font-normal normal-case tracking-normal w-[130px]" data-testid="users-filter-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="teacher">Enseignant</SelectItem>
                  <SelectItem value="parent">Parent</SelectItem>
                </SelectContent>
              </Select>
            </th>
            <th className="px-6 py-2">{filterInput(telF, setTelF, "Filtrer…", "users-filter-tel")}</th>
            <th className="px-6 py-2">
              <Select value={compF} onValueChange={setCompF}>
                <SelectTrigger className="h-8 text-xs rounded-lg bg-white border-[#EAE6DF] font-normal normal-case tracking-normal w-[150px]" data-testid="users-filter-comp">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="yes">Dédommagé</SelectItem>
                  <SelectItem value="no">Non dédommagé</SelectItem>
                </SelectContent>
              </Select>
            </th>
            <th className="px-6 py-2 text-right">
              {(nomF || emailF || telF || roleF !== "all" || compF !== "all") && (
                <button
                  onClick={() => { setNomF(""); setEmailF(""); setTelF(""); setRoleF("all"); setCompF("all"); }}
                  className="text-xs text-[#1E3A8A] hover:underline font-normal normal-case tracking-normal whitespace-nowrap"
                  data-testid="users-filters-clear"
                >
                  Effacer
                </button>
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 && <tr><td colSpan="6" className="px-6 py-10 text-center text-sm text-[#5c6374]">Aucun utilisateur ne correspond à la recherche.</td></tr>}
          {filtered.map((u) => (
            <tr key={u.id} className="border-b border-[#EAE6DF] last:border-0">
              <td className="px-6 py-3.5 text-sm">
                {u.name}
                <SignatureBadges u={u} />
              </td>
              <td className="px-6 py-3.5 text-sm">{u.email}</td>
              <td className="px-6 py-3.5 text-sm">
                {ROLE_LABELS[u.role] || u.role}
                {u.role === "parent" && u.is_teacher && (
                  <Badge className="ml-1.5 bg-[#E0F2E9] text-[#1E7A4D] border-0 rounded-full text-[10px]">+ Enseignant</Badge>
                )}
              </td>
              <td className="px-6 py-3.5 text-sm">{u.phone || "—"}</td>
              <td className="px-6 py-3.5 text-sm">
                {(u.role === "teacher" || u.is_teacher) ? <CompensationDialog teacher={u} onSaved={onChanged} /> : "—"}
              </td>
              <td className="px-6 py-3.5 text-sm flex gap-1 items-center">
                <EditUserDialog u={u} onSaved={onChanged} />
                {u.role === "parent" && (
                  <Button variant="ghost" size="sm" onClick={async () => {
                    const msg = u.is_teacher
                      ? `Retirer le rôle enseignant de ${u.name} ?`
                      : `Permettre à ${u.name} d'enseigner aussi (affectable aux cours, accès Espace enseignant) ?`;
                    if (!window.confirm(msg)) return;
                    try {
                      await api.put(`/users/${u.id}/teacher-flag`, { is_teacher: !u.is_teacher });
                      toast.success(u.is_teacher ? "Rôle enseignant retiré" : "Peut désormais enseigner");
                      onChanged();
                    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                  }} data-testid={`teacher-flag-${u.id}`} title={u.is_teacher ? "Retirer le rôle enseignant" : "Enseigne aussi"}>
                    <GraduationCap className={`w-4 h-4 ${u.is_teacher ? "text-[#1E7A4D]" : "text-[#8a92a3]"}`} />
                  </Button>
                )}
                {(u.role === "parent" || u.role === "teacher") && (
                  <>
                    <CredentialHistoryButton uid={u.id} email={u.email} />
                    <Button variant="ghost" size="sm" onClick={async () => {
                      if (!window.confirm(`Réinitialiser le mot de passe de ${u.email} ?`)) return;
                      try {
                        const r = await api.post(`/users/${u.id}/reset-password`);
                        window.alert(`Nouveau mot de passe temporaire pour ${u.email} :\n\n${r.data.temp_password}\n\n(Communiquez-le à l'utilisateur. Il pourra le modifier à sa prochaine connexion.)`);
                      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                    }} data-testid={`reset-password-${u.id}`} title="Réinitialiser le mot de passe">
                      <KeyRound className="w-4 h-4 text-[#1E3A8A]" />
                    </Button>
                  </>
                )}
                {u.role !== "admin" && (
                  <Button variant="ghost" size="sm" onClick={async () => {
                    if (!window.confirm(`Supprimer "${u.name}" ?`)) return;
                    await api.delete(`/users/${u.id}`); toast.success("Supprimé"); onChanged();
                  }} data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4 text-[#B0413E]" /></Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function SignatureBadges({ u }) {
  const items = [];
  if (u.role === "parent") items.push({ label: "RI", at: u.signed_ri_at });
  if (u.role === "teacher" || u.is_teacher) items.push({ label: "Charte", at: u.signed_charte_at });
  if (items.length === 0) return null;
  return (
    <div className="flex gap-1 mt-1" data-testid={`signature-badges-${u.id}`}>
      {items.map((it) => (
        <SignatureDetailDialog key={it.label} u={u} trigger={
          <button
            className={`text-[10px] px-1.5 py-0.5 rounded-full border cursor-pointer ${it.at ? "bg-[#E0F2E9] text-[#1E7A4D] border-[#1E7A4D]/20" : "bg-[#FBE5E4] text-[#B0413E] border-[#B0413E]/20"}`}
            title={it.at ? `Signé le ${new Date(it.at).toLocaleDateString("fr-FR")} — cliquer pour le détail` : "Non signé"}
          >
            {it.label} {it.at ? "✓" : "✗"}
          </button>
        } />
      ))}
    </div>
  );
}

function SignatureDetailDialog({ u, trigger }) {
  const [open, setOpen] = useState(false);
  const [sigs, setSigs] = useState(null);
  useEffect(() => {
    if (open) api.get(`/users/${u.id}/signatures`).then((r) => setSigs(r.data)).catch(() => setSigs([]));
  }, [open, u.id]);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Documents signés — {u.name}</DialogTitle></DialogHeader>
        {sigs === null && <p className="text-sm text-[#5c6374]">Chargement…</p>}
        {sigs?.length === 0 && <p className="text-sm text-[#5c6374]">Aucun document signé pour le moment.</p>}
        <div className="space-y-2">
          {(sigs || []).map((s) => (
            <div key={s.id} className="border border-[#EAE6DF] rounded-xl p-3 text-sm" data-testid={`signature-item-${s.kind}`}>
              <div className="font-semibold text-[#1A2547]">{s.document_title}</div>
              <div className="text-xs text-[#5c6374] mt-0.5">
                Signé « {s.signed_name} » le {new Date(s.signed_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ u, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: u.name || "", email: u.email || "", phone: u.phone || "" });
  useEffect(() => {
    if (open) setForm({ name: u.name || "", email: u.email || "", phone: u.phone || "" });
  }, [open, u]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/users/${u.id}`, form);
      toast.success("Utilisateur mis à jour");
      setOpen(false); onSaved();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" title="Modifier l'utilisateur" data-testid={`edit-user-${u.id}`}>
          <Pencil className="w-4 h-4 text-[#1E3A8A]" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Modifier {u.name}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="text-sm">Nom</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1.5" data-testid="edit-user-name" /></div>
          <div>
            <Label className="text-sm">Email</Label>
            <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl mt-1.5" data-testid="edit-user-email" />
            {form.email.trim().toLowerCase() !== (u.email || "").toLowerCase() && (
              <p className="text-xs text-[#C86444] mt-1">⚠ L'email sert d'identifiant de connexion : l'utilisateur devra se connecter avec ce nouvel email.</p>
            )}
          </div>
          <div><Label className="text-sm">Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl mt-1.5" data-testid="edit-user-phone" /></div>
          <div className="text-xs text-[#5c6374] bg-[#FCFBF8] border border-[#EAE6DF] rounded-xl p-3">
            Rôle : <strong>{ROLE_LABELS[u.role] || u.role}</strong> (non modifiable — utilisez « Enseigne aussi » pour la double casquette, ou la réinitialisation pour le mot de passe)
          </div>
          <Button type="submit" className="w-full rounded-full bg-[#1E3A8A] hover:bg-[#14265E]" data-testid="edit-user-submit">Enregistrer</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CompensationDialog({ teacher, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ is_compensated: !!teacher.is_compensated, hourly_rate: teacher.hourly_rate ?? "" });

  useEffect(() => {
    if (open) setForm({ is_compensated: !!teacher.is_compensated, hourly_rate: teacher.hourly_rate ?? "" });
  }, [open, teacher]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/users/${teacher.id}/compensation`, {
        is_compensated: form.is_compensated,
        hourly_rate: form.is_compensated ? parseFloat(form.hourly_rate || "0") : null,
      });
      toast.success("Dédommagement mis à jour");
      setOpen(false);
      onSaved();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="rounded-full text-xs" data-testid={`compensation-${teacher.id}`}>
          {teacher.is_compensated
            ? <Badge className="bg-[#E0F2E9] text-[#1E7A4D] border-0 rounded-full">✓ {teacher.hourly_rate ?? 0} €/h</Badge>
            : <Badge className="bg-[#F4F1EB] text-[#5c6374] border-0 rounded-full">Non dédommagé</Badge>}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Dédommagement — {teacher.name}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="flex items-start gap-2 bg-[#FCFBF8] border border-[#EAE6DF] rounded-xl p-3">
            <input
              id={`comp-${teacher.id}`}
              type="checkbox"
              checked={form.is_compensated}
              onChange={(e) => setForm({ ...form, is_compensated: e.target.checked })}
              className="mt-0.5"
              data-testid="comp-toggle"
            />
            <label htmlFor={`comp-${teacher.id}`} className="text-sm text-[#1A2547] cursor-pointer">
              Cet enseignant est <strong>dédommagé financièrement</strong> pour ses heures de cours.
            </label>
          </div>
          {form.is_compensated && (
            <div>
              <Label className="text-sm">Tarif horaire (€)</Label>
              <Input
                type="number"
                step="0.5"
                min="0"
                required
                value={form.hourly_rate}
                onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })}
                placeholder="Ex: 25"
                className="rounded-xl mt-1.5"
                data-testid="comp-rate"
              />
              <p className="text-xs text-[#5c6374] mt-1.5">
                Ce tarif sera figé au moment où le prof déclare une heure (modifier le tarif n'impacte pas les saisies passées).
              </p>
            </div>
          )}
          <Button type="submit" className="w-full rounded-full bg-[#1E3A8A] hover:bg-[#14265E]" data-testid="comp-submit">Enregistrer</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function GroupsTab({ groups, requests, onSaved }) {
  // Liste locale ordonnée + état de drag (index)
  const [localOrder, setLocalOrder] = useState(groups);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});  // { [groupId]: bool }
  const [search, setSearch] = useState("");

  // Resync quand le parent recharge
  useEffect(() => { setLocalOrder(groups); }, [groups]);

  const onDragStart = (index) => (e) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
    // nécessaire pour Firefox
    // setData peut throw sur certains anciens navigateurs ; uniquement requis pour Firefox legacy → ignorer en silence
    try { e.dataTransfer.setData("text/plain", String(index)); } catch (legacyErr) { logger.debug("dataTransfer.setData unsupported:", legacyErr); }
  };
  const onDragOver = (index) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (overIndex !== index) setOverIndex(index);
  };
  const onDragLeave = () => setOverIndex(null);
  const onDragEnd = () => { setDragIndex(null); setOverIndex(null); };

  const persistOrder = async (next) => {
    setSaving(true);
    try {
      await api.post("/groups/reorder", { ordered_ids: next.map((g) => g.id) });
      toast.success("Ordre des groupes mis à jour");
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setLocalOrder(groups); // rollback
    } finally {
      setSaving(false);
    }
  };

  const onDrop = (index) => (e) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) { onDragEnd(); return; }
    const next = [...localOrder];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(index, 0, moved);
    setLocalOrder(next);
    onDragEnd();
    persistOrder(next);
  };

  const downloadClasses = async (fmt) => {
    try {
      const r = await api.get(`/admin/export/classes.${fmt}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: fmt === "pdf" ? "application/pdf" : "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `classes_${new Date().toISOString().slice(0, 10)}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} téléchargé`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <p className="text-sm text-[#5C6861]">
          Définissez librement les groupes/classes de votre association. Chaque groupe inclut automatiquement les 3 matières.
          <span className="block text-xs text-[#8a92a3] mt-1">Glissez-déposez les groupes par la poignée <GripVertical className="inline w-3 h-3 align-middle" /> pour réorganiser leur ordre d'affichage.</span>
        </p>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => downloadClasses("csv")} data-testid="export-classes-csv">
            <Download className="w-4 h-4 mr-1.5" /> Liste CSV
          </Button>
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => downloadClasses("pdf")} data-testid="export-classes-pdf">
            <Download className="w-4 h-4 mr-1.5" /> Liste PDF
          </Button>
          <GroupDialog onSaved={onSaved} />
        </div>
      </div>
      <div className="mb-4 flex">
        <SearchBar value={search} onChange={setSearch} placeholder="Rechercher un groupe ou un élève…" testId="groups-search" />
      </div>
      {localOrder.length === 0 && <Empty msg="Aucun groupe défini. Cliquez sur « Nouveau groupe » pour commencer." />}
      <div className="space-y-2" data-testid="groups-list">
        {(() => {
          const q = search.trim().toLowerCase();
          const searching = q.length > 0;
          const displayed = searching
            ? localOrder.filter((g) => {
                if ([g.name, g.description].filter(Boolean).join(" ").toLowerCase().includes(q)) return true;
                return (requests || []).some((r) =>
                  r.status === "validated" && r.assigned_group_id === g.id &&
                  `${r.enf_prenom || ""} ${r.enf_nom || ""}`.toLowerCase().includes(q)
                );
              })
            : localOrder;
          if (searching && displayed.length === 0) return <Empty msg="Aucun groupe ne correspond à la recherche." />;
          return displayed.map((g) => {
          const index = localOrder.indexOf(g);
          const isDragging = dragIndex === index;
          const isOver = overIndex === index && dragIndex !== null && dragIndex !== index;
          const isOpen = !!expanded[g.id];
          // Liste des élèves attribués à ce groupe (demandes validées de l'année courante, déjà filtrée par le sélecteur d'année)
          const students = (requests || [])
            .filter((r) => r.status === "validated" && r.assigned_group_id === g.id)
            .sort((a, b) => {
              const an = `${a.enf_prenom || ""} ${a.enf_nom || ""}`.toLowerCase();
              const bn = `${b.enf_prenom || ""} ${b.enf_nom || ""}`.toLowerCase();
              return an.localeCompare(bn);
            });
          return (
            <div
              key={g.id}
              className={`bg-white border rounded-2xl transition-all ${
                isDragging ? "opacity-40 border-dashed border-[#1E3A8A]" : isOver ? "border-[#1E3A8A] border-2 shadow-md" : "border-[#EAE6DF]"
              } ${saving ? "cursor-wait" : ""}`}
              data-testid={`group-${g.id}`}
            >
              <div
                draggable={!saving && !searching}
                onDragStart={onDragStart(index)}
                onDragOver={onDragOver(index)}
                onDragLeave={onDragLeave}
                onDrop={onDrop(index)}
                onDragEnd={onDragEnd}
                className="p-4 flex items-center gap-3"
              >
                {!searching && (
                <div
                  className="cursor-grab active:cursor-grabbing text-[#8a92a3] hover:text-[#1A2547] touch-none shrink-0"
                  title="Glisser pour réorganiser"
                  data-testid={`group-handle-${g.id}`}
                >
                  <GripVertical className="w-5 h-5" strokeWidth={1.5} />
                </div>
                )}
                <div className="w-9 h-9 rounded-xl bg-[#E0E7FF] flex items-center justify-center text-[#1E3A8A] shrink-0">
                  <Layers className="w-4 h-4" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-[Outfit] font-semibold text-base text-[#1A2547] truncate">{g.name}</span>
                    <Badge className="bg-[#F4F1EB] text-[#5c6374] border-0 rounded-full text-[10px]">#{index + 1}</Badge>
                    <Badge className="bg-[#E0E7FF] text-[#1E3A8A] border-0 rounded-full text-[10px]">{students.length} élève{students.length > 1 ? "s" : ""}</Badge>
                  </div>
                  {g.description && <p className="text-xs text-[#5C6861] mt-0.5 truncate">{g.description}</p>}
                </div>
                <div className="flex gap-1 shrink-0 items-center">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0 text-[#5c6374] hover:text-[#1A2547]"
                    onClick={() => setExpanded((prev) => ({ ...prev, [g.id]: !prev[g.id] }))}
                    title={isOpen ? "Masquer les élèves" : "Voir les élèves"}
                    data-testid={`group-toggle-students-${g.id}`}
                    aria-expanded={isOpen}
                  >
                    {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </Button>
                  <GroupDialog group={g} onSaved={onSaved} />
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={async () => {
                    if (!window.confirm(`Supprimer le groupe « ${g.name} » et ses 3 cours ?`)) return;
                    try {
                      await api.delete(`/groups/${g.id}`);
                      toast.success("Groupe supprimé");
                      onSaved();
                    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                  }} data-testid={`delete-group-${g.id}`}>
                    <Trash2 className="w-4 h-4 text-[#B0413E]" />
                  </Button>
                </div>
              </div>
              {isOpen && (
                <div className="border-t border-[#EAE6DF] bg-[#FCFBF8] px-4 py-3" data-testid={`group-students-${g.id}`}>
                  {students.length === 0 ? (
                    <div className="text-xs text-[#8a92a3] py-2 text-center">Aucun élève attribué à ce groupe pour cette année.</div>
                  ) : (
                    <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1.5">
                      {students.map((s) => (
                        <li
                          key={s.id}
                          className="flex items-center gap-2 bg-white border border-[#EAE6DF] rounded-xl px-2.5 py-1.5"
                          data-testid={`group-student-${g.id}-${s.id}`}
                        >
                          <div className="w-6 h-6 rounded-lg bg-[#E0E7FF] text-[#1E3A8A] flex items-center justify-center shrink-0">
                            <GraduationCap className="w-3.5 h-3.5" strokeWidth={1.5} />
                          </div>
                          <span className="text-sm text-[#1A2547] truncate flex-1">{s.enf_prenom} {s.enf_nom}</span>
                          {s.enf_age != null && (
                            <span className="text-[10px] text-[#5c6374] bg-[#F4F1EB] rounded-full px-1.5 py-0.5 shrink-0">{s.enf_age} ans</span>
                          )}
                          <StudentMoveSelect
                            student={s}
                            groups={groups}
                            currentGroupId={g.id}
                            onMoved={onSaved}
                          />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          );
          });
        })()}
      </div>
    </div>
  );
}

function GroupDialog({ group, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: group?.name || "", description: group?.description || "", order: group?.order ?? 0 });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { name: form.name, description: form.description || null, order: parseInt(form.order) || 0 };
      if (group) {
        await api.put(`/groups/${group.id}`, payload);
        toast.success("Groupe mis à jour");
      } else {
        await api.post("/groups", payload);
        toast.success("Groupe créé (3 cours générés)");
      }
      setOpen(false);
      onSaved();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {group ? (
          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`edit-group-${group.id}`}>
            <Pencil className="w-4 h-4 text-[#1E3A8A]" />
          </Button>
        ) : (
          <Button className="rounded-full bg-[#1E3A8A] hover:bg-[#14265E]" data-testid="add-group-btn"><Plus className="w-4 h-4 mr-1.5" /> Nouveau groupe</Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>{group ? "Modifier le groupe" : "Créer un groupe"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="text-sm">Nom *</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1.5" data-testid="group-name" placeholder="Ex: Groupe Enfants 6-8 ans" />
          </div>
          <div><Label className="text-sm">Description</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-1.5" placeholder="Optionnel" />
          </div>
          <div><Label className="text-sm">Ordre d'affichage</Label>
            <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="rounded-xl mt-1.5" />
          </div>
          {!group && <p className="text-xs text-[#8a968f]">Les 3 cours (Arabe, Coran, Éducation Musulmane) seront créés automatiquement avec un horaire par défaut que vous pourrez modifier.</p>}
          <Button type="submit" className="w-full rounded-full bg-[#1E3A8A] hover:bg-[#14265E]" data-testid="group-submit">{group ? "Enregistrer" : "Créer"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function VacationsTab({ vacations, onSaved }) {
  const [form, setForm] = useState({ name: "", start_date: "", end_date: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (form.end_date < form.start_date) return toast.error("La date de fin doit être après le début");
    try {
      await api.post("/vacations", form);
      toast.success("Période ajoutée");
      setForm({ name: "", start_date: "", end_date: "" });
      onSaved();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
  };
  const remove = async (vid) => {
    if (!window.confirm("Supprimer cette période ?")) return;
    await api.delete(`/vacations/${vid}`);
    toast.success("Supprimée"); onSaved();
  };
  return (
    <div className="space-y-6">
      <div className="bg-white border border-[#EAE6DF] rounded-2xl p-6">
        <h3 className="font-[Outfit] font-semibold text-lg text-[#1A2547] mb-3">Nouvelle période sans cours</h3>
        <p className="text-sm text-[#5c6374] mb-4">Ajoutez les vacances scolaires, jours fériés ou fermetures exceptionnelles. Ces dates seront grisées dans le planning.</p>
        <form onSubmit={submit} className="grid sm:grid-cols-4 gap-3 items-end">
          <div className="sm:col-span-2"><Label className="text-sm">Intitulé</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Vacances de Noël" className="rounded-xl mt-1.5" data-testid="vacation-name" /></div>
          <div><Label className="text-sm">Début</Label><Input required type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-xl mt-1.5" data-testid="vacation-start" /></div>
          <div><Label className="text-sm">Fin</Label><Input required type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-xl mt-1.5" data-testid="vacation-end" /></div>
          <Button type="submit" className="rounded-full bg-[#1E3A8A] hover:bg-[#14265E] sm:col-span-4" data-testid="vacation-submit">Ajouter</Button>
        </form>
      </div>
      <div className="bg-white border border-[#EAE6DF] rounded-2xl overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead><tr className="border-b border-[#EAE6DF]">
            {["Période", "Du", "Au", ""].map((h) => <th key={h} className="text-left px-6 py-3 text-xs uppercase tracking-[0.18em] font-semibold text-[#8a92a3]">{h}</th>)}
          </tr></thead>
          <tbody>
            {vacations.length === 0 && <tr><td colSpan="4" className="px-6 py-10 text-center text-[#5c6374] text-sm">Aucune période enregistrée.</td></tr>}
            {vacations.map((v) => (
              <tr key={v.id} className="border-b border-[#EAE6DF] last:border-0" data-testid={`vacation-${v.id}`}>
                <td className="px-6 py-3.5 text-sm font-medium">{v.name}</td>
                <td className="px-6 py-3.5 text-sm">{new Date(v.start_date).toLocaleDateString("fr-FR")}</td>
                <td className="px-6 py-3.5 text-sm">{new Date(v.end_date).toLocaleDateString("fr-FR")}</td>
                <td className="px-6 py-3.5 text-sm">
                  <Button variant="ghost" size="sm" onClick={() => remove(v.id)} data-testid={`delete-vacation-${v.id}`}><Trash2 className="w-4 h-4 text-[#B0413E]" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MeetingsTab({ meetings, onSaved }) {
  const empty = { title: "", date: "", start_time: "", end_time: "", location: "", description: "" };
  const [form, setForm] = useState(empty);
  const submit = async (e) => {
    e.preventDefault();
    if (form.end_time <= form.start_time) return toast.error("L'heure de fin doit être après l'heure de début");
    try {
      await api.post("/meetings", form);
      toast.success("Réunion ajoutée — visible par tous les parents");
      setForm(empty); onSaved();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
  };
  const remove = async (mid) => {
    if (!window.confirm("Supprimer cette réunion ?")) return;
    await api.delete(`/meetings/${mid}`);
    toast.success("Supprimée"); onSaved();
  };
  const upcoming = meetings.filter((m) => m.date >= new Date().toISOString().slice(0, 10));
  const past = meetings.filter((m) => m.date < new Date().toISOString().slice(0, 10));
  const renderRows = (list, dataPrefix) => list.length === 0
    ? <tr><td colSpan="5" className="px-6 py-10 text-center text-[#5c6374] text-sm">Aucune réunion.</td></tr>
    : list.map((m) => (
      <tr key={m.id} className="border-b border-[#EAE6DF] last:border-0" data-testid={`${dataPrefix}-${m.id}`}>
        <td className="px-6 py-3.5 text-sm font-medium text-[#1A2547]">{m.title}</td>
        <td className="px-6 py-3.5 text-sm">{new Date(m.date).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</td>
        <td className="px-6 py-3.5 text-sm">{m.start_time}–{m.end_time}</td>
        <td className="px-6 py-3.5 text-sm">{m.location || "—"}{m.description && <div className="text-xs text-[#5c6374] italic mt-0.5">« {m.description} »</div>}</td>
        <td className="px-6 py-3.5 text-sm">
          <Button variant="ghost" size="sm" onClick={() => remove(m.id)} data-testid={`delete-meeting-${m.id}`}><Trash2 className="w-4 h-4 text-[#B0413E]" /></Button>
        </td>
      </tr>
    ));

  return (
    <div className="space-y-6" data-testid="meetings-tab">
      <div className="bg-white border border-[#EAE6DF] rounded-2xl p-6">
        <h3 className="font-[Outfit] font-semibold text-lg text-[#1A2547] mb-3">Nouvelle réunion parents–profs</h3>
        <p className="text-sm text-[#5c6374] mb-4">Cette réunion sera visible dans le planning de tous les parents.</p>
        <form onSubmit={submit} className="grid sm:grid-cols-6 gap-3 items-end">
          <div className="sm:col-span-3"><Label className="text-sm">Intitulé *</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex: Réunion parents-profs T1" className="rounded-xl mt-1.5" data-testid="meeting-title" /></div>
          <div className="sm:col-span-3"><Label className="text-sm">Lieu (optionnel)</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Ex: Salle polyvalente" className="rounded-xl mt-1.5" data-testid="meeting-location" /></div>
          <div className="sm:col-span-2"><Label className="text-sm">Date *</Label><Input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl mt-1.5" data-testid="meeting-date" /></div>
          <div className="sm:col-span-2"><Label className="text-sm">Heure début *</Label><Input required type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="rounded-xl mt-1.5" data-testid="meeting-start" /></div>
          <div className="sm:col-span-2"><Label className="text-sm">Heure fin *</Label><Input required type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="rounded-xl mt-1.5" data-testid="meeting-end" /></div>
          <div className="sm:col-span-6"><Label className="text-sm">Description (optionnel)</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="rounded-xl mt-1.5" placeholder="Précisions, ordre du jour…" /></div>
          <Button type="submit" className="rounded-full bg-[#1E3A8A] hover:bg-[#14265E] sm:col-span-6" data-testid="meeting-submit">Ajouter la réunion</Button>
        </form>
      </div>

      <div className="bg-white border border-[#EAE6DF] rounded-2xl overflow-x-auto">
        <div className="px-6 py-4 border-b border-[#EAE6DF]">
          <h3 className="font-[Outfit] font-semibold text-base text-[#1A2547]">À venir ({upcoming.length})</h3>
        </div>
        <table className="w-full min-w-[640px]">
          <thead><tr className="border-b border-[#EAE6DF] bg-[#FCFBF8]">
            {["Réunion", "Date", "Horaires", "Lieu / Détails", ""].map((h) => <th key={h} className="text-left px-6 py-3 text-xs uppercase tracking-[0.18em] font-semibold text-[#8a92a3]">{h}</th>)}
          </tr></thead>
          <tbody>{renderRows(upcoming, "meeting-up")}</tbody>
        </table>
      </div>

      {past.length > 0 && (
        <div className="bg-white border border-[#EAE6DF] rounded-2xl overflow-x-auto opacity-75">
          <div className="px-6 py-4 border-b border-[#EAE6DF]">
            <h3 className="font-[Outfit] font-semibold text-base text-[#1A2547]">Passées ({past.length})</h3>
          </div>
        <table className="w-full min-w-[640px]">
            <thead><tr className="border-b border-[#EAE6DF] bg-[#FCFBF8]">
              {["Réunion", "Date", "Horaires", "Lieu / Détails", ""].map((h) => <th key={h} className="text-left px-6 py-3 text-xs uppercase tracking-[0.18em] font-semibold text-[#8a92a3]">{h}</th>)}
            </tr></thead>
            <tbody>{renderRows(past, "meeting-past")}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function UserDialog({ onSaved }) {
  const [open, setOpen] = useState(false);
  const empty = { name: "", email: "", password: "", role: "teacher", phone: "", autoPwd: true };
  const [form, setForm] = useState(empty);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name,
        email: form.email,
        role: form.role,
        phone: form.phone,
      };
      // Si autoPwd, on n'envoie pas de password (backend en génère un).
      // Pour admin, on impose un password manuel (pas de must_change pour admin).
      if (!form.autoPwd || form.role === "admin") {
        payload.password = form.password;
      }
      const r = await api.post("/users", payload);
      if (r.data?.temp_password) {
        window.alert(
          `Compte ${form.role === "teacher" ? "enseignant" : "utilisateur"} créé !\n\n` +
          `Email : ${r.data.email}\n` +
          `Mot de passe temporaire : ${r.data.temp_password}\n\n` +
          `(Communiquez-lui ces identifiants. Il sera invité à changer son mot de passe à sa 1re connexion.)`
        );
      } else {
        toast.success("Utilisateur créé");
      }
      setOpen(false); setForm(empty); onSaved();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full bg-[#1E3A8A] hover:bg-[#14265E]" data-testid="add-user-btn"><Plus className="w-4 h-4 mr-1.5" /> Nouvel utilisateur</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Créer un utilisateur</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label className="text-sm">Nom</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1.5" data-testid="user-name" /></div>
          <div><Label className="text-sm">Email</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl mt-1.5" data-testid="user-email" /></div>
          <div><Label className="text-sm">Rôle</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, autoPwd: v !== "admin" })}>
              <SelectTrigger className="rounded-xl mt-1.5" data-testid="user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="teacher">Enseignant</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.role !== "admin" && (
            <div className="flex items-start gap-2 bg-[#FCFBF8] border border-[#EAE6DF] rounded-xl p-3">
              <input
                id="auto-pwd"
                type="checkbox"
                checked={form.autoPwd}
                onChange={(e) => setForm({ ...form, autoPwd: e.target.checked })}
                className="mt-0.5"
                data-testid="user-auto-pwd"
              />
              <label htmlFor="auto-pwd" className="text-sm text-[#1A2547] cursor-pointer">
                Générer automatiquement un mot de passe temporaire <span className="text-[#5c6374]">(recommandé — l'utilisateur le changera à sa 1re connexion)</span>
              </label>
            </div>
          )}
          {(form.role === "admin" || !form.autoPwd) && (
            <div>
              <Label className="text-sm">Mot de passe {form.role === "admin" ? "" : "manuel"}</Label>
              <Input required minLength={6} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-xl mt-1.5" data-testid="user-password" />
            </div>
          )}
          <div><Label className="text-sm">Téléphone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl mt-1.5" /></div>
          <Button type="submit" className="w-full rounded-full bg-[#1E3A8A] hover:bg-[#14265E]" data-testid="user-submit">Créer</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function AttendanceTab({ groups, courses }) {
  const [groupId, setGroupId] = useState("");
  const [courseId, setCourseId] = useState("");
  const [studentSearch, setStudentSearch] = useState("");
  const [data, setData] = useState({ records: [], stats: { total: 0, present: 0, absent: 0, retard: 0, presence_rate: null } });
  const [loading, setLoading] = useState(false);

  const filteredCourses = groupId ? courses.filter((c) => c.group_id === groupId) : courses;

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = {};
    if (courseId && courseId !== "all") params.course_id = courseId;
    else if (groupId && groupId !== "all") params.group_id = groupId;
    api.get("/admin/attendance", { params })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setLoading(false));
  }, [groupId, courseId]);
  useEffect(() => { fetchData(); }, [fetchData]);

  // Per-student aggregated stats (exclude orphan records whose enrollment was deleted)
  const byStudent = {};
  for (const r of data.records) {
    if (!r.student?.name) continue;
    const sid = r.student_request_id;
    if (!byStudent[sid]) byStudent[sid] = { name: r.student.name, group_id: r.student.group_id, records: [] };
    byStudent[sid].records.push(r);
  }
  const studentRows = Object.entries(byStudent).map(([sid, s]) => {
    const total = s.records.length;
    const present = s.records.filter((r) => r.status === "present").length;
    const absent = s.records.filter((r) => r.status === "absent").length;
    const retard = s.records.filter((r) => r.status === "retard").length;
    const rate = total > 0 ? Math.round(((present + retard) / total) * 100) : null;
    return { sid, ...s, total, present, absent, retard, rate };
  })
    .filter((s) => !studentSearch.trim() || s.name.toLowerCase().includes(studentSearch.trim().toLowerCase()))
    .sort((a, b) => (a.rate ?? 0) - (b.rate ?? 0)); // worst attendance first

  const STATUS = {
    present: { label: "Présent", style: "bg-[#E0F2E9] text-[#1E7A4D]" },
    absent: { label: "Absent", style: "bg-[#FBE5E4] text-[#B0413E]" },
    retard: { label: "Retard", style: "bg-[#FFF5E5] text-[#C86444]" },
  };

  return (
    <div className="space-y-6" data-testid="admin-attendance-tab">
      {/* Filters */}
      <div className="bg-white border border-[#EAE6DF] rounded-2xl p-5">
        <h3 className="font-[Outfit] font-semibold text-lg text-[#1A2547] mb-3">Vue d'ensemble des présences</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-sm">Groupe</Label>
            <Select value={groupId || "all"} onValueChange={(v) => { setGroupId(v === "all" ? "" : v); setCourseId(""); }}>
              <SelectTrigger className="rounded-xl mt-1.5" data-testid="admin-att-group"><SelectValue placeholder="Tous les groupes" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les groupes</SelectItem>
                {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Cours</Label>
            <Select value={courseId || "all"} onValueChange={(v) => setCourseId(v === "all" ? "" : v)}>
              <SelectTrigger className="rounded-xl mt-1.5" data-testid="admin-att-course"><SelectValue placeholder="Tous les cours" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les cours</SelectItem>
                {filteredCourses.map((c) => <SelectItem key={c.id} value={c.id}>{ML[c.matiere]} — {c.group_name || ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Global stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Taux présence global", value: data.stats.presence_rate != null ? `${data.stats.presence_rate}%` : "—", color: "#1E3A8A" },
          { label: "Présent", value: data.stats.present, color: "#1E7A4D" },
          { label: "Retards", value: data.stats.retard, color: "#C86444" },
          { label: "Absences", value: data.stats.absent, color: "#B0413E" },
        ].map((t) => (
          <div key={t.label} className="bg-white border border-[#EAE6DF] rounded-2xl p-5" data-testid={`admin-att-stat-${t.label}`}>
            <div className="text-2xl md:text-3xl font-[Outfit] font-semibold" style={{ color: t.color }}>{t.value}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8a92a3] mt-1.5">{t.label}</div>
          </div>
        ))}
      </div>

      {/* Per-student table */}
      <div className="bg-white border border-[#EAE6DF] rounded-2xl overflow-x-auto">
        <div className="px-6 py-4 border-b border-[#EAE6DF] flex flex-wrap items-center gap-3">
          <h3 className="font-[Outfit] font-semibold text-base text-[#1A2547]">Par élève ({studentRows.length})</h3>
          <SearchBar value={studentSearch} onChange={setStudentSearch} placeholder="Rechercher un élève…" testId="attendance-student-search" />
          {loading && <span className="text-xs text-[#8a92a3]">Chargement…</span>}
        </div>
        <table className="w-full min-w-[640px]">
          <thead><tr className="border-b border-[#EAE6DF] bg-[#FCFBF8]">
            {["Élève", "Taux", "Présent", "Retards", "Absences", "Total"].map((h) => (
              <th key={h} className="text-left px-6 py-3 text-xs uppercase tracking-[0.18em] font-semibold text-[#8a92a3]">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {studentRows.length === 0 && <tr><td colSpan="6" className="px-6 py-10 text-center text-sm text-[#5c6374]">Aucune présence enregistrée pour ce filtre.</td></tr>}
            {studentRows.map((s) => (
              <tr key={s.sid} className="border-b border-[#EAE6DF] last:border-0" data-testid={`admin-att-student-${s.sid}`}>
                <td className="px-6 py-3.5 text-sm font-medium text-[#1A2547]">{s.name}</td>
                <td className="px-6 py-3.5 text-sm">
                  <Badge className={`border-0 rounded-full ${s.rate >= 90 ? "bg-[#E0F2E9] text-[#1E7A4D]" : s.rate >= 70 ? "bg-[#FFF5E5] text-[#C86444]" : "bg-[#FBE5E4] text-[#B0413E]"}`}>
                    {s.rate != null ? `${s.rate}%` : "—"}
                  </Badge>
                </td>
                <td className="px-6 py-3.5 text-sm text-[#1E7A4D]">{s.present}</td>
                <td className="px-6 py-3.5 text-sm text-[#C86444]">{s.retard}</td>
                <td className="px-6 py-3.5 text-sm text-[#B0413E]">{s.absent}</td>
                <td className="px-6 py-3.5 text-sm text-[#5c6374]">{s.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Recent records */}
      <div className="bg-white border border-[#EAE6DF] rounded-2xl overflow-x-auto">
        <div className="px-6 py-4 border-b border-[#EAE6DF]">
          <h3 className="font-[Outfit] font-semibold text-base text-[#1A2547]">Saisies récentes ({Math.min(50, data.records.length)})</h3>
        </div>
        <table className="w-full min-w-[640px]">
          <thead><tr className="border-b border-[#EAE6DF] bg-[#FCFBF8]">
            {["Date", "Élève", "Matière", "Statut", "Motif"].map((h) => (
              <th key={h} className="text-left px-6 py-3 text-xs uppercase tracking-[0.18em] font-semibold text-[#8a92a3]">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {data.records.length === 0 && <tr><td colSpan="5" className="px-6 py-10 text-center text-sm text-[#5c6374]">Rien à afficher.</td></tr>}
            {data.records.slice(0, 50).map((r) => (
              <tr key={r.id || `${r.student_id}-${r.date}-${r.matiere}`} className="border-b border-[#EAE6DF] last:border-0">
                <td className="px-6 py-3 text-sm">{new Date(r.date).toLocaleDateString("fr-FR")}</td>
                <td className="px-6 py-3 text-sm">{r.student?.name || "—"}</td>
                <td className="px-6 py-3 text-sm">{ML[r.matiere] || "—"}</td>
                <td className="px-6 py-3 text-sm">
                  <Badge className={`border-0 rounded-full ${STATUS[r.status]?.style}`}>{STATUS[r.status]?.label}</Badge>
                </td>
                <td className="px-6 py-3 text-xs text-[#5c6374] italic">{r.note ? `« ${r.note} »` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function RequestsToolbar({ requests, groups, statusFilter, onFilterChange }) {
  const [contacts, setContacts] = useState(null);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);

  const counts = {
    all: requests.length,
    pending: requests.filter((r) => r.status === "pending").length,
    validated: requests.filter((r) => r.status === "validated").length,
    rejected: requests.filter((r) => r.status === "rejected").length,
  };

  const downloadCsv = async () => {
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const r = await api.get(`/enrollment-requests/export.csv${params}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `demandes_${statusFilter}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV téléchargé");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openMailing = async () => {
    setLoadingContacts(true);
    try {
      const r = await api.get("/enrollment-requests/mailing", { params: { status: "validated" } });
      setContacts(r.data);
      setContactsOpen(true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoadingContacts(false); }
  };

  const copyAllEmails = () => {
    const emails = (contacts || []).map((c) => c.email).join(", ");
    navigator.clipboard.writeText(emails).then(() => toast.success(`${contacts.length} email(s) copié(s)`));
  };

  return (
    <div className="bg-white border border-[#EAE6DF] rounded-2xl p-4 mb-4 flex flex-wrap items-center justify-between gap-3" data-testid="requests-toolbar">
      <div className="flex flex-wrap gap-2">
        {[
          { k: "all", label: `Toutes (${counts.all})` },
          { k: "pending", label: `En attente (${counts.pending})` },
          { k: "validated", label: `Validées (${counts.validated})` },
          { k: "rejected", label: `Rejetées (${counts.rejected})` },
        ].map((f) => (
          <button key={f.k} onClick={() => onFilterChange(f.k)}
            className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${statusFilter === f.k ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white border-[#EAE6DF] text-[#5c6374] hover:border-[#1E3A8A]"}`}
            data-testid={`filter-${f.k}`}>
            {f.label}
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="rounded-full" onClick={downloadCsv} data-testid="export-csv">
          <Download className="w-4 h-4 mr-1.5" /> Export CSV
        </Button>
        <Button size="sm" className="rounded-full bg-[#1E3A8A] hover:bg-[#14265E]" onClick={openMailing} disabled={loadingContacts} data-testid="open-mailing">
          <Mail className="w-4 h-4 mr-1.5" /> Mailing parents
        </Button>
      </div>

      <Dialog open={contactsOpen} onOpenChange={setContactsOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Contacts parents — Inscriptions validées</DialogTitle></DialogHeader>
          {contacts && (
            <>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-[#5c6374]"><strong>{contacts.length}</strong> parent(s) unique(s){contacts.length > 0 && ` · ${contacts.reduce((s, c) => s + c.children.length, 0)} enfant(s) au total`}</p>
                {contacts.length > 0 && (
                  <Button size="sm" variant="outline" className="rounded-full" onClick={copyAllEmails} data-testid="copy-emails">
                    <Copy className="w-4 h-4 mr-1.5" /> Copier tous les emails
                  </Button>
                )}
              </div>
              {contacts.length === 0 && <div className="text-sm text-[#5c6374] py-6 text-center">Aucun parent validé pour le moment.</div>}
              <div className="space-y-1.5 mt-2 max-h-[60vh] overflow-y-auto pr-1">
                {contacts.map((c) => (
                  <div key={c.email} className="bg-[#FCFBF8] border border-[#EAE6DF] rounded-lg p-3" data-testid={`contact-${c.email}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="min-w-0">
                        <div className="font-medium text-sm text-[#1A2547] truncate">{c.parent_name}</div>
                        <a href={`mailto:${c.email}`} className="text-xs text-[#1E3A8A] underline">{c.email}</a>
                        {c.phone && <span className="text-xs text-[#8a92a3] ml-2">· {c.phone}</span>}
                      </div>
                      <Badge className="bg-[#E0E7FF] text-[#1E3A8A] border-0 rounded-full text-[10px]">{c.children.length} enfant(s)</Badge>
                    </div>
                    <div className="text-xs text-[#5c6374] mt-1">{c.children.join(" · ")}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function AdminMessagesModeration() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/admin/messages/threads").then((r) => setThreads(r.data)).catch(() => {});
  }, []);

  const openThread = async (t) => {
    setActive(t);
    setLoading(true);
    try {
      const r = await api.get(`/admin/messages/thread/${encodeURIComponent(t.thread_key)}`);
      setMessages(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-white border border-[#EAE6DF] rounded-2xl p-6" data-testid="admin-moderation">
      <h3 className="font-[Outfit] font-semibold text-lg text-[#1A2547] mb-1">Modération · Toutes les conversations</h3>
      <p className="text-sm text-[#5c6374] mb-4">Vue lecture seule de toutes les conversations parents ↔ enseignants.</p>
      {threads.length === 0 && <div className="text-sm text-[#5c6374] text-center py-8">Aucune conversation pour l'instant.</div>}
      {threads.length > 0 && (
        <div className="grid md:grid-cols-2 gap-2">
          {threads.map((t) => (
            <button key={t.thread_key} onClick={() => openThread(t)}
              className={`text-left p-3 rounded-xl border transition-colors ${active?.thread_key === t.thread_key ? "border-[#1E3A8A] bg-[#FCFBF8]" : "border-[#EAE6DF] hover:border-[#1E3A8A]"}`}
              data-testid={`mod-thread-${t.thread_key}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-sm font-medium text-[#1A2547]">
                  {(t.participants || []).map((p) => p.name).join(" ↔ ")}
                </div>
                <Badge className="bg-[#E0E7FF] text-[#1E3A8A] border-0 rounded-full text-[10px]">{t.count} msg</Badge>
              </div>
              <div className="text-xs text-[#5c6374] mt-1 truncate">{t.last_message?.body}</div>
              <div className="text-[10px] text-[#8a92a3] mt-0.5">{new Date(t.last_message?.created_at).toLocaleString("fr-FR")}</div>
            </button>
          ))}
        </div>
      )}
      <Dialog open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setMessages([]); } }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {(active?.participants || []).map((p) => `${p.name} (${p.role})`).join(" ↔ ")}
            </DialogTitle>
          </DialogHeader>
          {loading && <div className="text-center text-sm text-[#5c6374] py-6">Chargement…</div>}
          {!loading && (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
              {messages.map((m) => (
                <div key={m.id} className="p-3 rounded-xl bg-[#FCFBF8] border border-[#EAE6DF]" data-testid={`mod-msg-${m.id}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-sm font-medium text-[#1A2547]">{m.sender_name}</span>
                    <Badge className="bg-[#E0E7FF] text-[#1E3A8A] border-0 rounded-full text-[10px]">{m.sender_role}</Badge>
                    <span className="text-[10px] text-[#8a92a3]">{new Date(m.created_at).toLocaleString("fr-FR")}</span>
                  </div>
                  <div className="text-sm whitespace-pre-wrap text-[#1A2547]">{m.body}</div>
                  {m.attachment_path && <div className="text-xs text-[#1E3A8A] mt-1">📎 {m.attachment_name || "Pièce jointe"}</div>}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CredentialHistoryButton({ uid, email }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);

  const load = async () => {
    setItems(null);
    try {
      const r = await api.get(`/users/${uid}/credential-history`);
      setItems(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setItems([]); }
  };

  const copy = (pwd) => {
    navigator.clipboard.writeText(pwd).then(() => toast.success("Mot de passe copié"));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" data-testid={`credential-history-${uid}`} title="Historique des identifiants envoyés">
          <History className="w-4 h-4 text-[#5c6374]" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Historique des identifiants — {email}</DialogTitle></DialogHeader>
        {items === null && <div className="text-sm text-[#5c6374] py-6 text-center">Chargement…</div>}
        {items !== null && items.length === 0 && (
          <div className="text-sm text-[#5c6374] py-8 text-center">Aucune trace pour cet utilisateur.</div>
        )}
        {items !== null && items.length > 0 && (
          <div className="space-y-2">
            {items.map((it) => (
              <div key={it.id} className="bg-[#FCFBF8] border border-[#EAE6DF] rounded-xl p-3" data-testid={`credential-row-${it.id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <Badge className={`border-0 rounded-full text-[10px] ${it.kind === "creation" ? "bg-[#E0F2E9] text-[#1E7A4D]" : "bg-[#E0E7FF] text-[#1E3A8A]"}`}>
                    {it.kind === "creation" ? "Création" : "Réinitialisation"}
                  </Badge>
                  <span className="text-xs text-[#8a92a3]">{new Date(it.created_at).toLocaleString("fr-FR")}</span>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-[#8a92a3]">Mot de passe :</span>
                  <code className="bg-[#E0E7FF] text-[#1E3A8A] px-2 py-1 rounded-md text-sm font-mono">{it.temp_password}</code>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => copy(it.temp_password)} title="Copier">
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <div className="mt-1 text-xs text-[#5c6374]">Par : {it.by_user_name || "Système"}</div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function CompensationTab({ users }) {
  const teachers = users.filter((u) => u.role === "teacher" || u.is_teacher);
  const compensated = teachers.filter((u) => u.is_compensated);
  const [teacherFilter, setTeacherFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState({ records: [], stats: { total_hours: 0, validated_hours: 0, pending_hours: 0, validated_amount: 0, pending_amount: 0 } });
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = { month };
    if (teacherFilter !== "all") params.teacher_id = teacherFilter;
    if (statusFilter !== "all") params.status = statusFilter;
    api.get("/admin/work-hours", { params })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setLoading(false));
  }, [teacherFilter, statusFilter, month]);
  useEffect(() => { load(); }, [load]);

  const validate = async (wid) => {
    try { await api.post(`/work-hours/${wid}/validate`); toast.success("Validé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reject = async (wid) => {
    const reason = window.prompt("Motif du rejet (optionnel) :");
    if (reason === null) return;
    try { await api.post(`/work-hours/${wid}/reject`, { reason: reason || null }); toast.success("Rejeté"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const markPaid = async (wid) => {
    if (!window.confirm("Marquer ce dédommagement comme payé ?")) return;
    try { await api.post(`/work-hours/${wid}/pay`); toast.success("Marqué payé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const unmarkPaid = async (wid) => {
    if (!window.confirm("Annuler le statut payé et revenir à « Validée » ?")) return;
    try { await api.post(`/work-hours/${wid}/unpay`); toast.success("Statut payé annulé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (wid) => {
    if (!window.confirm("Supprimer définitivement cette saisie ?")) return;
    try { await api.delete(`/work-hours/${wid}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadCsv = async () => {
    try {
      const params = { month };
      if (teacherFilter !== "all") params.teacher_id = teacherFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const r = await api.get("/admin/work-hours/export.csv", { params, responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `heures_${month}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("CSV téléchargé");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const STATUS = {
    pending: { label: "En attente", style: "bg-[#FFF5E5] text-[#C86444]" },
    validated: { label: "Validée", style: "bg-[#E0F2E9] text-[#1E7A4D]" },
    rejected: { label: "Rejetée", style: "bg-[#FBE5E4] text-[#B0413E]" },
    paid: { label: "Payée", style: "bg-[#E0E7FF] text-[#1E3A8A]" },
  };

  return (
    <div className="space-y-6" data-testid="compensation-tab">
      {/* Filters */}
      <div className="bg-white border border-[#EAE6DF] rounded-2xl p-5">
        <h3 className="font-[Outfit] font-semibold text-lg text-[#1A2547] mb-3">Heures déclarées</h3>
        <p className="text-sm text-[#5c6374] mb-4">
          {compensated.length} enseignant(s) dédommagé(s) sur {teachers.length}. Activez le dédommagement depuis l'onglet Utilisateurs.
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-sm">Enseignant</Label>
            <Select value={teacherFilter} onValueChange={setTeacherFilter}>
              <SelectTrigger className="rounded-xl mt-1.5" data-testid="comp-filter-teacher"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                {teachers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Statut</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="rounded-xl mt-1.5" data-testid="comp-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="validated">Validées</SelectItem>
                <SelectItem value="paid">Payées</SelectItem>
                <SelectItem value="rejected">Rejetées</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Mois</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-xl mt-1.5" data-testid="comp-month" />
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {[
          { label: "Heures validées", value: `${data.stats.validated_hours} h`, color: "#1E7A4D" },
          { label: "Heures en attente", value: `${data.stats.pending_hours} h`, color: "#C86444" },
          { label: "Heures payées", value: `${(data.stats.paid_hours ?? 0)} h`, color: "#1E3A8A" },
          { label: "Montant à payer", value: `${data.stats.validated_amount.toFixed(2)} €`, color: "#1E7A4D" },
          { label: "Montant payé", value: `${(data.stats.paid_amount ?? 0).toFixed(2)} €`, color: "#1E3A8A" },
        ].map((t) => (
          <div key={t.label} className="bg-white border border-[#EAE6DF] rounded-2xl p-5">
            <div className="text-2xl md:text-3xl font-[Outfit] font-semibold" style={{ color: t.color }}>{t.value}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8a92a3] mt-1.5">{t.label}</div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex justify-end">
        <Button size="sm" variant="outline" className="rounded-full" onClick={downloadCsv} data-testid="comp-export-csv">
          <Download className="w-4 h-4 mr-1.5" /> Export CSV
        </Button>
      </div>

      {/* Records table */}
      <div className="bg-white border border-[#EAE6DF] rounded-2xl overflow-x-auto">
        <div className="px-6 py-4 border-b border-[#EAE6DF] flex items-center justify-between">
          <h3 className="font-[Outfit] font-semibold text-base text-[#1A2547]">Saisies ({data.records.length})</h3>
          {loading && <span className="text-xs text-[#8a92a3]">Chargement…</span>}
        </div>
        <table className="w-full min-w-[640px]">
          <thead><tr className="border-b border-[#EAE6DF] bg-[#FCFBF8]">
            {["Date", "Enseignant", "Cours / Note", "Heures", "Montant", "Statut", ""].map((h) => (
              <th key={h} className="text-left px-6 py-3 text-xs uppercase tracking-[0.18em] font-semibold text-[#8a92a3]">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {data.records.length === 0 && (
              <tr><td colSpan="7" className="px-6 py-10 text-center text-sm text-[#5c6374]">Aucune heure déclarée pour ce filtre.</td></tr>
            )}
            {data.records.map((r) => (
              <tr key={r.id} className="border-b border-[#EAE6DF] last:border-0" data-testid={`comp-row-${r.id}`}>
                <td className="px-6 py-3 text-sm">{new Date(r.date).toLocaleDateString("fr-FR")}</td>
                <td className="px-6 py-3 text-sm">{r.teacher_name}</td>
                <td className="px-6 py-3 text-sm">
                  <div className="text-[#1A2547]">{r.course_label || "—"}</div>
                  {r.note && <div className="text-xs text-[#5c6374] italic">« {r.note} »</div>}
                  {r.rejection_reason && <div className="text-xs text-[#B0413E] italic">Rejet : {r.rejection_reason}</div>}
                </td>
                <td className="px-6 py-3 text-sm">{r.hours.toFixed(2)} h</td>
                <td className="px-6 py-3 text-sm font-medium text-[#1A2547]">{(r.amount_snapshot || 0).toFixed(2)} €</td>
                <td className="px-6 py-3 text-sm">
                  <Badge className={`border-0 rounded-full ${STATUS[r.status]?.style}`}>{STATUS[r.status]?.label}</Badge>
                </td>
                <td className="px-6 py-3 text-sm flex gap-1">
                  {r.status === "pending" && (
                    <>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => validate(r.id)} data-testid={`comp-validate-${r.id}`} title="Valider">
                        <CheckCircle2 className="w-4 h-4 text-[#1E7A4D]" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => reject(r.id)} data-testid={`comp-reject-${r.id}`} title="Rejeter">
                        <XCircle className="w-4 h-4 text-[#B0413E]" />
                      </Button>
                    </>
                  )}
                  {r.status === "validated" && (
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => markPaid(r.id)} data-testid={`comp-pay-${r.id}`} title="Marquer payé">
                      <Banknote className="w-4 h-4 text-[#1E3A8A]" />
                    </Button>
                  )}
                  {r.status === "paid" && (
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => unmarkPaid(r.id)} data-testid={`comp-unpay-${r.id}`} title="Annuler le statut payé">
                      <Undo2 className="w-4 h-4 text-[#8a92a3]" />
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => remove(r.id)} data-testid={`comp-delete-${r.id}`} title="Supprimer">
                    <Trash2 className="w-4 h-4 text-[#B0413E]" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function SchoolYearSelector({ years, selectedYearId, activeYearId, onChange, onYearsChanged }) {
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({ label: "", start_date: "", end_date: "" });

  const create = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/school-years", form);
      toast.success(`Année ${r.data.label} créée`);
      setOpenCreate(false);
      setForm({ label: "", start_date: "", end_date: "" });
      onYearsChanged();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
  };

  const activate = async (yid) => {
    if (!window.confirm("Activer cette année scolaire ? L'année actuellement active deviendra une année passée.")) return;
    try {
      await api.post(`/school-years/${yid}/activate`);
      toast.success("Année activée");
      onYearsChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (yid, label) => {
    if (!window.confirm(`Supprimer définitivement l'année ${label} ?`)) return;
    try {
      await api.delete(`/school-years/${yid}`);
      toast.success("Année supprimée");
      onYearsChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog>
      <div className="flex items-center gap-1 bg-white border border-[#EAE6DF] rounded-full p-1 pl-3" data-testid="year-selector">
        <CalendarRange className="w-4 h-4 text-[#1E3A8A]" strokeWidth={2} />
        <Select value={selectedYearId || ""} onValueChange={onChange}>
          <SelectTrigger className="border-0 shadow-none h-9 px-2 focus:ring-0 min-w-[110px]" data-testid="year-select-trigger">
            <SelectValue placeholder="Année…" />
          </SelectTrigger>
          <SelectContent>
            {years.map((y) => (
              <SelectItem key={y.id} value={y.id}>
                {y.label}{y.is_active ? " · Active" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <DialogTrigger asChild>
          <Button variant="ghost" size="sm" className="h-9 w-9 p-0 rounded-full" title="Gérer les années" data-testid="year-manage-btn">
            <Pencil className="w-3.5 h-3.5 text-[#5c6374]" />
          </Button>
        </DialogTrigger>
      </div>

      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Années scolaires</DialogTitle>
          <DialogDescription>Créer, activer ou supprimer une année scolaire. Une seule année peut être active à la fois.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {years.map((y) => (
            <div key={y.id} className="flex items-center justify-between gap-2 bg-[#FCFBF8] border border-[#EAE6DF] rounded-xl p-3" data-testid={`year-row-${y.id}`}>
              <div>
                <div className="font-medium text-[#1A2547] flex items-center gap-2">
                  {y.label}
                  {y.is_active && <Badge className="bg-[#E0F2E9] text-[#1E7A4D] border-0 rounded-full text-[10px]">Active</Badge>}
                </div>
                <div className="text-xs text-[#5c6374]">{y.start_date} → {y.end_date}</div>
              </div>
              <div className="flex gap-1">
                {!y.is_active && y.id !== activeYearId && (
                  <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => activate(y.id)} data-testid={`year-activate-${y.id}`}>
                    Activer
                  </Button>
                )}
                {!y.is_active && (
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => remove(y.id, y.label)} data-testid={`year-delete-${y.id}`}>
                    <Trash2 className="w-4 h-4 text-[#B0413E]" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        <YearExtras year={years.find((y) => y.is_active)} onSaved={onYearsChanged} />

        <form onSubmit={create} className="space-y-3 pt-4 border-t border-[#EAE6DF]">
          <h3 className="font-[Outfit] font-semibold text-sm text-[#1A2547]">Créer une nouvelle année</h3>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-3 sm:col-span-1">
              <Label className="text-xs">Libellé</Label>
              <Input required value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="2026/2027" className="rounded-xl mt-1" data-testid="year-label" />
            </div>
            <div className="col-span-3 sm:col-span-1">
              <Label className="text-xs">Début</Label>
              <Input required type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-xl mt-1" data-testid="year-start" />
            </div>
            <div className="col-span-3 sm:col-span-1">
              <Label className="text-xs">Fin</Label>
              <Input required type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-xl mt-1" data-testid="year-end" />
            </div>
          </div>
          <Button type="submit" size="sm" className="w-full rounded-full bg-[#1E3A8A] hover:bg-[#14265E]" data-testid="year-create-submit">
            <Plus className="w-4 h-4 mr-1.5" /> Créer
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function weekendLabel(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    const dow = d.getDay(); // 0=dim, 6=sam
    const sat = new Date(d);
    sat.setDate(d.getDate() + (dow === 0 ? -1 : 6 - dow));
    const sun = new Date(sat);
    sun.setDate(sat.getDate() + 1);
    const fmt = (x, withMonth) => x.getDate() + (withMonth ? " " + x.toLocaleDateString("fr-FR", { month: "long" }) : "");
    const sameMonth = sat.getMonth() === sun.getMonth();
    return `samedi ${fmt(sat, !sameMonth)} et dimanche ${sun.getDate()} ${sun.toLocaleDateString("fr-FR", { month: "long" })} ${sun.getFullYear()}`;
  } catch {
    return iso;
  }
}

function YearExtras({ year, onSaved }) {
  const [rentree, setRentree] = useState(year?.rentree_date || "");
  const [perms, setPerms] = useState(year?.payment_permanences || []);
  const [start, setStart] = useState(year?.start_date || "");
  const [end, setEnd] = useState(year?.end_date || "");
  useEffect(() => {
    setRentree(year?.rentree_date || "");
    setPerms(year?.payment_permanences || []);
    setStart(year?.start_date || "");
    setEnd(year?.end_date || "");
  }, [year?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!year) return null;
  const save = async () => {
    if (start && end && start >= end) { toast.error("La date de début doit être avant la date de fin"); return; }
    try {
      await api.put(`/school-years/${year.id}`, {
        start_date: start,
        end_date: end,
        rentree_date: rentree || null,
        payment_permanences: perms.filter((p) => p.date),
      });
      toast.success("Paramètres de l'année enregistrés");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="pt-4 border-t border-[#EAE6DF] space-y-3" data-testid="year-extras">
      <h3 className="font-[Outfit] font-semibold text-sm text-[#1A2547]">Paramètres de l'année active ({year.label})</h3>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Début de l'année</Label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-xl mt-1" data-testid="year-start-date" />
        </div>
        <div>
          <Label className="text-xs">Fin de l'année</Label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="rounded-xl mt-1" data-testid="year-end-date" />
        </div>
      </div>
      <p className="text-xs text-[#5c6374]">Le planning n'affiche les cours que dans cette plage — en dehors, les jours sont marqués « Hors période scolaire ».</p>
      <div>
        <Label className="text-xs">Week-end de rentrée (choisissez le samedi)</Label>
        <Input type="date" value={rentree} onChange={(e) => setRentree(e.target.value)} className="rounded-xl mt-1" data-testid="rentree-date" />
        {rentree && (
          <p className="text-xs text-[#1E7A4D] mt-1" data-testid="rentree-weekend-preview">
            → Rentrée : {weekendLabel(rentree)} (samedi ou dimanche selon le jour de cours de l'enfant)
          </p>
        )}
      </div>
      <div>
        <Label className="text-xs">Permanences de paiement (date + horaires)</Label>
        <div className="space-y-2 mt-1">
          {perms.map((p, i) => (
            <div key={i} className="flex gap-2 items-center">
              <Input type="date" value={p.date || ""} onChange={(e) => setPerms(perms.map((x, j) => j === i ? { ...x, date: e.target.value } : x))} className="rounded-xl flex-1" data-testid={`perm-date-${i}`} />
              <Input value={p.time || ""} onChange={(e) => setPerms(perms.map((x, j) => j === i ? { ...x, time: e.target.value } : x))} placeholder="ex : de 10h00 à 12h00" className="rounded-xl flex-1" data-testid={`perm-time-${i}`} />
              <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" onClick={() => setPerms(perms.filter((_, j) => j !== i))} data-testid={`perm-remove-${i}`}>
                <Trash2 className="w-4 h-4 text-[#B0413E]" />
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" className="rounded-full text-xs" onClick={() => setPerms([...perms, { date: "", time: "" }])} data-testid="perm-add">
            <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter une permanence
          </Button>
        </div>
      </div>
      <p className="text-xs text-[#5c6374] bg-[#FCFBF8] border border-[#EAE6DF] rounded-xl p-2.5">
        Ces informations sont incluses dans l'email de validation envoyé aux parents : date de rentrée (avec mention
        que le planning est disponible sur leur compte), permanences de paiement, et avertissement qu'une inscription
        non réglée lors d'une permanence sera annulée.
      </p>
      <Button type="button" size="sm" onClick={save} className="w-full rounded-full bg-[#1E3A8A] hover:bg-[#14265E]" data-testid="year-extras-save">
        Enregistrer
      </Button>
    </div>
  );
}

// ===== Élèves (admin) =====
function StudentsTab({ requests, groups, groupName, courses = [], onSaved }) {
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("all");
  const [sortAge, setSortAge] = useState("asc"); // asc | desc
  const [splitOnly, setSplitOnly] = useState(false);

  const validated = requests.filter((r) => r.status === "validated");

  // Jours de cours réels d'un élève : cours de son groupe filtrés par ses matières suivies
  const studentDays = (r) => {
    const mats = r.assigned_matieres || [];
    const ds = new Set();
    for (const c of courses) {
      if (c.group_id !== r.assigned_group_id) continue;
      if (mats.length > 0 && !mats.includes(c.matiere)) continue;
      const d = (c.schedule_day || "").trim().toLowerCase();
      if (d) ds.add(d);
    }
    return [...ds].sort();
  };

  // Fratries : même nom de famille + même adresse
  const fratMap = {};
  for (const r of validated) {
    const nom = (r.enf_nom || "").trim().toLowerCase();
    const adr = (r.enf_adresse || "").trim().toLowerCase().replace(/\s+/g, " ");
    if (!nom || !adr) continue;
    const k = `${nom}|${adr}`;
    (fratMap[k] = fratMap[k] || []).push(r);
  }
  // Fratries réparties sur des jours différents (ex: un enfant le samedi, l'autre le dimanche)
  const splitInfo = {}; // request_id -> { siblings: [...], days: [...] }
  for (const list of Object.values(fratMap)) {
    if (list.length < 2) continue;
    const daysUnion = new Set();
    const sets = list.map((r) => {
      const ds = studentDays(r);
      ds.forEach((d) => daysUnion.add(d));
      return ds.join(",");
    });
    const allSame = sets.every((s) => s === sets[0]);
    if (daysUnion.size > 1 && !allSame) {
      for (const r of list) {
        splitInfo[r.id] = {
          days: [...daysUnion].sort(),
          siblings: list.filter((x) => x.id !== r.id).map((x) => `${x.enf_prenom} (${studentDays(x).join(", ") || "jour ?"})`),
        };
      }
    }
  }
  const splitFratries = Object.values(fratMap).filter((l) => l.length >= 2 && splitInfo[l[0].id]).length;

  const filtered = validated
    .filter((r) => {
      if (splitOnly && !splitInfo[r.id]) return false;
      if (groupFilter !== "all" && r.assigned_group_id !== groupFilter) return false;
      if (!search.trim()) return true;
      const q = search.trim().toLowerCase();
      const hay = [r.enf_prenom, r.enf_nom, r.rl_prenom, r.rl_nom, r.rl_email, r.enf_ville]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    })
    .sort((a, b) => {
      if (splitOnly) {
        const cmp = (a.enf_nom || "").localeCompare(b.enf_nom || "");
        if (cmp !== 0) return cmp;
      }
      const aa = parseInt(a.enf_age) || 0;
      const bb = parseInt(b.enf_age) || 0;
      return sortAge === "asc" ? aa - bb : bb - aa;
    });

  return (
    <div data-testid="students-tab">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <SearchBar value={search} onChange={setSearch} placeholder="Rechercher un élève ou un parent…" testId="students-search" />
        <Select value={groupFilter} onValueChange={setGroupFilter}>
          <SelectTrigger className="rounded-full bg-white border-[#EAE6DF] w-[200px]" data-testid="students-group-filter">
            <SelectValue placeholder="Groupe" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les groupes</SelectItem>
            {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setSortAge((s) => s === "asc" ? "desc" : "asc")}
          className="rounded-full h-10"
          data-testid="students-sort-age"
        >
          {sortAge === "asc" ? <SortAsc className="w-4 h-4 mr-1.5" /> : <SortDesc className="w-4 h-4 mr-1.5" />}
          Âge {sortAge === "asc" ? "croissant" : "décroissant"}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setSplitOnly((s) => !s)}
          className={`rounded-full h-10 ${splitOnly ? "bg-[#FBE8DC] border-[#C86444]/40 text-[#C86444] hover:bg-[#FBE8DC]" : Object.keys(splitInfo).length > 0 ? "border-[#C86444]/40 text-[#C86444]" : ""}`}
          data-testid="students-split-filter"
        >
          <AlertTriangle className="w-4 h-4 mr-1.5" />
          Fratries sur 2 jours ({splitFratries})
        </Button>
      </div>

      {splitOnly && (
        <div className="bg-[#FBE8DC] border border-[#C86444]/30 rounded-2xl p-3 mb-4 text-xs text-[#5c6374] leading-relaxed" data-testid="split-info-banner">
          <strong className="text-[#C86444]">Fratries réparties sur des jours différents</strong> — enfants de la même famille
          (même nom + même adresse) dont les groupes ont cours des jours différents (ex. un enfant le samedi, l'autre le dimanche).
          Pensez à les regrouper sur la même journée si possible.
        </div>
      )}

      <div className="text-xs text-[#5c6374] mb-3" data-testid="students-count">
        {filtered.length} élève{filtered.length > 1 ? "s" : ""} {validated.length !== filtered.length && <span>(sur {validated.length})</span>}
      </div>

      {filtered.length === 0 ? (
        <Empty msg={validated.length === 0 ? "Aucun élève validé pour l'année active." : "Aucun élève ne correspond à la recherche."} />
      ) : (
        <div className="space-y-2">
          {filtered.map((r) => (
            <StudentRow key={r.id} req={r} groupName={groupName} groups={groups} onSaved={onSaved} split={splitInfo[r.id]} days={studentDays(r)} />
          ))}
        </div>
      )}
    </div>
  );
}

function StudentRow({ req, groupName, groups, onSaved, split, days = [] }) {
  return (
    <div className={`bg-white border rounded-2xl p-4 flex flex-wrap items-center gap-3 ${split ? "border-[#C86444]/40" : "border-[#EAE6DF]"}`} data-testid={`student-row-${req.id}`}>
      <div className="w-10 h-10 rounded-xl bg-[#E0E7FF] flex items-center justify-center text-[#1E3A8A] shrink-0">
        <GraduationCap className="w-5 h-5" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-[200px]">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-[Outfit] font-semibold text-[#1A2547]">{req.enf_prenom} {req.enf_nom}</span>
          {req.enf_age && <Badge className="bg-[#F4F1EB] text-[#5c6374] border-0 rounded-full text-[10px]">{req.enf_age} ans</Badge>}
          {req.is_reenrollment && (
            <Badge className="bg-[#FBE8DC] text-[#C86444] border-0 rounded-full text-[10px] inline-flex items-center gap-1">
              <GraduationCap className="w-3 h-3" /> Réinscription
            </Badge>
          )}
          {split && (
            <Badge
              className="bg-[#FBE8DC] text-[#C86444] border border-[#C86444]/30 rounded-full text-[10px] inline-flex items-center gap-1"
              title={`Fratrie répartie sur : ${split.days.join(" et ")}. Frère(s)/sœur(s) : ${split.siblings.join(" · ")}`}
              data-testid={`split-badge-${req.id}`}
            >
              <AlertTriangle className="w-3 h-3" /> Fratrie sur {split.days.length} jours
            </Badge>
          )}
        </div>
        <div className="text-xs text-[#5c6374] mt-0.5">
          {req.enf_ville || "—"} · {groupName(req.assigned_group_id)}{days.length > 0 && <span className="capitalize"> ({days.join(", ")})</span>} · {(req.assigned_matieres || []).map((m) => ML[m]).join(", ") || "—"}
        </div>
        {split && (
          <div className="text-[11px] text-[#C86444] mt-1" data-testid={`split-siblings-${req.id}`}>
            Fratrie : {split.siblings.join(" · ")}
          </div>
        )}
      </div>
      <div className="text-xs text-[#5c6374] min-w-[200px]">
        <div className="font-medium text-[#1A2547]">{req.rl_prenom} {req.rl_nom}</div>
        <div className="truncate">{req.rl_email} · {req.rl_tel_portable}</div>
      </div>
      <EditStudentDialog req={req} groups={groups} onSaved={onSaved} />
    </div>
  );
}

function EditStudentDialog({ req, groups, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(req);
  const [saving, setSaving] = useState(false);

  // Réinitialiser le form quand on ouvre
  useEffect(() => {
    if (open) setForm(req);
  }, [open, req]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleMat = (k) => {
    const cur = form.matieres_suivies || [];
    set("matieres_suivies", cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Construire le payload : ne renvoyer que ce qui a changé n'est pas critique ici, on renvoie tout
      const payload = {
        rl_prenom: form.rl_prenom, rl_nom: form.rl_nom,
        rl_tel_fixe: form.rl_tel_fixe || null, rl_tel_portable: form.rl_tel_portable,
        rl_email: form.rl_email,
        rl_participe_activites_mosquee: !!form.rl_participe_activites_mosquee,
        rl_activites_mosquee: form.rl_activites_mosquee || null,
        rl_prieres_regulieres_mosquee: !!form.rl_prieres_regulieres_mosquee,
        enf_prenom: form.enf_prenom, enf_nom: form.enf_nom,
        enf_adresse: form.enf_adresse, enf_ville: form.enf_ville,
        enf_date_naissance: form.enf_date_naissance,
        enf_age: form.enf_age ? parseInt(form.enf_age) : null,
        enf_autorisation_rentrer_seul: !!form.enf_autorisation_rentrer_seul,
        a_des_freres_soeurs: !!form.a_des_freres_soeurs,
        freres_soeurs_details: form.freres_soeurs_details || null,
        premiere_inscription: !!form.premiere_inscription,
        cours_arabe_anterieur: form.cours_arabe_anterieur || null,
        groupe_souhaite: form.groupe_souhaite || null,
        niveau_arabe_ecrit: Number(form.niveau_arabe_ecrit) || 0,
        niveau_arabe_oral: Number(form.niveau_arabe_oral) || 0,
        nb_sourates: Number(form.nb_sourates) || 0,
        sourates_connues: Array.isArray(form.sourates_connues) ? form.sourates_connues : [],
        traitement_medical: !!form.traitement_medical,
        traitement_medical_detail: form.traitement_medical_detail || null,
        allergies: !!form.allergies,
        allergies_detail: form.allergies_detail || null,
        allergies_alimentaires: !!form.allergies_alimentaires,
        allergies_alimentaires_detail: form.allergies_alimentaires_detail || null,
        maladies_chroniques: !!form.maladies_chroniques,
        maladies_chroniques_detail: form.maladies_chroniques_detail || null,
        autres_renseignements: form.autres_renseignements || null,
        urg_prenom: form.urg_prenom, urg_nom: form.urg_nom,
        urg_tel_fixe: form.urg_tel_fixe || null, urg_tel_portable: form.urg_tel_portable,
        autorisation_photos_classe: !!form.autorisation_photos_classe,
        matieres_suivies: form.matieres_suivies || [],
      };
      await api.put(`/enrollment-requests/${req.id}`, payload);
      toast.success("Dossier élève mis à jour");
      setOpen(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full border-[#1E3A8A]/30 text-[#1E3A8A] hover:bg-[#E0E7FF]" data-testid={`student-edit-${req.id}`}>
          <UserCog className="w-3.5 h-3.5 mr-1.5" /> Modifier
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Modifier le dossier — {req.enf_prenom} {req.enf_nom}</DialogTitle>
          <DialogDescription>
            Mettre à jour les informations de l'élève, du représentant légal et des contacts d'urgence. Les modifications sont enregistrées immédiatement.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={save} className="space-y-5" data-testid="edit-student-form">
          <FieldSet title="Représentant légal">
            <Grid2col>
              <FF label="Prénom *"><Input required value={form.rl_prenom || ""} onChange={(e) => set("rl_prenom", e.target.value)} className="rounded-xl" data-testid="ed-rl_prenom" /></FF>
              <FF label="Nom *"><Input required value={form.rl_nom || ""} onChange={(e) => set("rl_nom", e.target.value)} className="rounded-xl" data-testid="ed-rl_nom" /></FF>
              <FF label="Téléphone portable *"><Input required value={form.rl_tel_portable || ""} onChange={(e) => set("rl_tel_portable", e.target.value)} className="rounded-xl" data-testid="ed-rl_tel_portable" /></FF>
              <FF label="Téléphone fixe"><Input value={form.rl_tel_fixe || ""} onChange={(e) => set("rl_tel_fixe", e.target.value)} className="rounded-xl" /></FF>
            </Grid2col>
            <FF label="Email *"><Input type="email" required value={form.rl_email || ""} onChange={(e) => set("rl_email", e.target.value)} className="rounded-xl" data-testid="ed-rl_email" /></FF>
            <div className="flex flex-wrap gap-4 pt-1">
              <CheckLabel checked={!!form.rl_participe_activites_mosquee} onChange={(v) => set("rl_participe_activites_mosquee", v)} label="Participe aux activités mosquée" />
              <CheckLabel checked={!!form.rl_prieres_regulieres_mosquee} onChange={(v) => set("rl_prieres_regulieres_mosquee", v)} label="Prières régulières à la mosquée" />
            </div>
            {form.rl_participe_activites_mosquee && (
              <FF label="Détails activités"><Input value={form.rl_activites_mosquee || ""} onChange={(e) => set("rl_activites_mosquee", e.target.value)} className="rounded-xl" /></FF>
            )}
          </FieldSet>

          <FieldSet title="Enfant">
            <Grid2col>
              <FF label="Prénom *"><Input required value={form.enf_prenom || ""} onChange={(e) => set("enf_prenom", e.target.value)} className="rounded-xl" data-testid="ed-enf_prenom" /></FF>
              <FF label="Nom *"><Input required value={form.enf_nom || ""} onChange={(e) => set("enf_nom", e.target.value)} className="rounded-xl" data-testid="ed-enf_nom" /></FF>
              <FF label="Date de naissance *"><Input required type="date" value={form.enf_date_naissance || ""} onChange={(e) => set("enf_date_naissance", e.target.value)} className="rounded-xl" data-testid="ed-enf_date_naissance" /></FF>
              <FF label="Âge"><Input type="number" value={form.enf_age || ""} onChange={(e) => set("enf_age", e.target.value)} className="rounded-xl" /></FF>
            </Grid2col>
            <FF label="Adresse *"><Input required value={form.enf_adresse || ""} onChange={(e) => set("enf_adresse", e.target.value)} className="rounded-xl" data-testid="ed-enf_adresse" /></FF>
            <FF label="Ville *"><Input required value={form.enf_ville || ""} onChange={(e) => set("enf_ville", e.target.value)} className="rounded-xl" data-testid="ed-enf_ville" /></FF>
            <CheckLabel checked={!!form.enf_autorisation_rentrer_seul} onChange={(v) => set("enf_autorisation_rentrer_seul", v)} label="Autorisé à rentrer seul après les cours" />
          </FieldSet>

          <FieldSet title="Fratrie & historique">
            <CheckLabel checked={!!form.a_des_freres_soeurs} onChange={(v) => set("a_des_freres_soeurs", v)} label="A des frères/sœurs dans l'association" />
            {form.a_des_freres_soeurs && (
              <FF label="Détails fratrie"><Textarea value={form.freres_soeurs_details || ""} onChange={(e) => set("freres_soeurs_details", e.target.value)} className="rounded-xl" rows={2} /></FF>
            )}
            <CheckLabel checked={!!form.premiere_inscription} onChange={(v) => set("premiere_inscription", v)} label="Première inscription dans l'association" />
            {!form.premiere_inscription && (
              <FF label="Cours d'arabe antérieurs"><Input value={form.cours_arabe_anterieur || ""} onChange={(e) => set("cours_arabe_anterieur", e.target.value)} className="rounded-xl" /></FF>
            )}
            <FF label="Groupe souhaité (info)"><Input value={form.groupe_souhaite || ""} onChange={(e) => set("groupe_souhaite", e.target.value)} className="rounded-xl" placeholder="Indication parent" /></FF>
          </FieldSet>

          <FieldSet title="Niveau d'arabe & Coran">
            <ArabicLevelBlock form={form} set={set} testIdPrefix="ed-arabe" />
          </FieldSet>

          <FieldSet title="Matières suivies">
            <div className="flex flex-wrap gap-3">
              {MATIERES.map((m) => (
                <label key={m.key} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#F4F1EB] border border-[#EAE6DF] cursor-pointer">
                  <Checkbox checked={(form.matieres_suivies || []).includes(m.key)} onCheckedChange={() => toggleMat(m.key)} data-testid={`ed-mat-${m.key}`} />
                  <span className="text-sm">{m.label}</span>
                </label>
              ))}
            </div>
            <div className="text-[10px] text-[#8a92a3] mt-1">Ce sont les matières souhaitées. Les matières effectivement attribuées (validation) se gèrent depuis l'onglet Demandes.</div>
          </FieldSet>

          <FieldSet title="Médical">
            <ToggleDetail checked={!!form.traitement_medical} onChange={(v) => set("traitement_medical", v)} label="Traitement médical" detail={form.traitement_medical_detail || ""} onDetail={(v) => set("traitement_medical_detail", v)} />
            <ToggleDetail checked={!!form.allergies} onChange={(v) => set("allergies", v)} label="Allergies" detail={form.allergies_detail || ""} onDetail={(v) => set("allergies_detail", v)} />
            <ToggleDetail checked={!!form.allergies_alimentaires} onChange={(v) => set("allergies_alimentaires", v)} label="Allergies alimentaires" detail={form.allergies_alimentaires_detail || ""} onDetail={(v) => set("allergies_alimentaires_detail", v)} />
            <ToggleDetail checked={!!form.maladies_chroniques} onChange={(v) => set("maladies_chroniques", v)} label="Maladies chroniques" detail={form.maladies_chroniques_detail || ""} onDetail={(v) => set("maladies_chroniques_detail", v)} />
          </FieldSet>

          <FieldSet title="Contact en cas d'urgence">
            <Grid2col>
              <FF label="Prénom *"><Input required value={form.urg_prenom || ""} onChange={(e) => set("urg_prenom", e.target.value)} className="rounded-xl" data-testid="ed-urg_prenom" /></FF>
              <FF label="Nom *"><Input required value={form.urg_nom || ""} onChange={(e) => set("urg_nom", e.target.value)} className="rounded-xl" data-testid="ed-urg_nom" /></FF>
              <FF label="Téléphone portable *"><Input required value={form.urg_tel_portable || ""} onChange={(e) => set("urg_tel_portable", e.target.value)} className="rounded-xl" data-testid="ed-urg_tel_portable" /></FF>
              <FF label="Téléphone fixe"><Input value={form.urg_tel_fixe || ""} onChange={(e) => set("urg_tel_fixe", e.target.value)} className="rounded-xl" /></FF>
            </Grid2col>
          </FieldSet>

          <FieldSet title="Autorisations & notes">
            <CheckLabel checked={!!form.autorisation_photos_classe} onChange={(v) => set("autorisation_photos_classe", v)} label="Autorise la prise de photos de classe" />
            <FF label="Autres renseignements"><Textarea value={form.autres_renseignements || ""} onChange={(e) => set("autres_renseignements", e.target.value)} className="rounded-xl" rows={3} /></FF>
          </FieldSet>

          <div className="flex justify-end gap-2 pt-2 border-t border-[#EAE6DF]">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="rounded-full" data-testid="edit-student-cancel">Annuler</Button>
            <Button type="submit" disabled={saving} className="rounded-full bg-[#1E3A8A] hover:bg-[#14265E] text-white" data-testid="edit-student-save">
              {saving ? "Enregistrement…" : "Enregistrer les modifications"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FieldSet({ title, children }) {
  return (
    <div className="bg-[#FCFBF8] border border-[#EAE6DF] rounded-2xl p-4 space-y-3">
      <h4 className="font-[Outfit] font-semibold text-sm text-[#1A2547]">{title}</h4>
      {children}
    </div>
  );
}
function Grid2col({ children }) { return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>; }
function FF({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-[#5c6374]">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
function CheckLabel({ checked, onChange, label }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} />
      <span className="text-sm text-[#1A2547]">{label}</span>
    </label>
  );
}
function ToggleDetail({ checked, onChange, label, detail, onDetail }) {
  return (
    <div>
      <CheckLabel checked={checked} onChange={onChange} label={label} />
      {checked && (
        <Input value={detail} onChange={(e) => onDetail(e.target.value)} placeholder="Détail…" className="rounded-xl mt-2" />
      )}
    </div>
  );
}
function StudentMoveSelect({ student, groups, currentGroupId, onMoved }) {
  const [saving, setSaving] = useState(false);
  const handleChange = async (newGid) => {
    if (newGid === currentGroupId || saving) return;
    const target = groups.find((g) => g.id === newGid);
    if (!target) return;
    if (!window.confirm(`Déplacer ${student.enf_prenom} ${student.enf_nom} vers « ${target.name} » ?`)) return;
    setSaving(true);
    try {
      await api.put(`/enrollment-requests/${student.id}/group`, { group_id: newGid });
      toast.success(`${student.enf_prenom} déplacé vers ${target.name}`);
      onMoved && onMoved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Select value={currentGroupId} onValueChange={handleChange} disabled={saving}>
      <SelectTrigger
        className="h-7 px-2 text-[10px] rounded-full bg-[#F4F1EB] border-0 hover:bg-[#E0E7FF] hover:text-[#1E3A8A] w-[34px] shrink-0"
        title="Déplacer vers un autre groupe"
        data-testid={`student-move-${student.id}`}
        aria-label="Déplacer l'élève vers un autre groupe"
      >
        {/* Pas de texte visible, juste le chevron natif */}
      </SelectTrigger>
      <SelectContent align="end">
        {groups.map((g) => (
          <SelectItem
            key={g.id}
            value={g.id}
            disabled={g.id === currentGroupId}
            data-testid={`student-move-${student.id}-target-${g.id}`}
          >
            {g.name}{g.id === currentGroupId ? " (actuel)" : ""}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

